// fx render seam — per-INSTANCE _render interception for live effects.
//
// THE ONE FILE that touches fabric render internals. Everything else (registry,
// effects, facade, UI) works with plain canvases and data bags.
//
// Fabric contract this rides (verified against fabric 6.9.1 dist):
//   Object.render(ctx): ctx.save → _setupCompositeOperation → transform(ctx) →
//     _setOpacity → _setShadow → shouldCache() ? renderCache+drawCacheOnCanvas
//     : drawObject(ctx,false,{}) → ctx.restore. drawObject calls this._render —
//     an INSTANCE _render property wins over the prototype. That is the seam.
//   Consequences we rely on:
//   - Native shadow/opacity/blend are applied on the OUTER ctx → they compose
//     over the fx result for free; effects must not re-implement them.
//   - shouldCache() sets this.ownCaching as a side effect right before
//     drawObject → inside _render, ownCaching === true means "we are drawing
//     into this object's own cache" (redrawn only when dirty/zoom changes),
//     false means direct ctx or a parent group's cache (may run EVERY frame —
//     curved text sets objectCaching:false) → stash-only mode, never run
//     filters synchronously there.
//   - _getCacheCanvasDimensions() is called via `this.` → instance override
//     adds the fx spill margin, else the cache clips soft edges. The Text
//     subclass already pads by fontSize (glyph overhang) — we add spill on top
//     of whatever the prototype returns.
//   - _updateCacheCanvas() redraws on dims/zoom change → zooming and the print
//     multiplier automatically re-run fx at the new resolution.
//   - toObject() emits type from the CLASS ('textbox') — a patched instance
//     still serializes as plain fabric JSON (exportability law: no custom
//     class, ever; an instance patch is not a class).
//   - drawObject(ctx, forClipping) forces fill black for clip renders — fx is
//     skipped there (a bevel's outer band must not grow a clip mask), via a
//     tiny instance drawObject wrapper that flags the call.
//
// Failure posture: a broken effect must never break typing, save or export.
// Every wrapper falls back to the prototype's render on throw; a failed
// self-test (canary) disables the whole system module-wide.

import { StaticCanvas, Textbox, config, util } from 'fabric';
import { registerFx, getFx } from './fxRegistry';
import { getPosterLightFor, stepsUsePosterLight } from './fxPosterLight';
import { bridgeDiecutPockets, diecutCloseRadiusPx } from '../raster/diecutClose.js';
import { diecutStrokeReach, glyphMaskWanted, contourOutlineWanted } from '../../inspector/text/effectDefs.js';
// Universal-effects U1: kind classification + the dual-read slot bag
// (data.effects on shapes/icons, data.textEffects on text) + the contour
// step id. Pure module — nothing heavy rides in.
import { objectKindOf, effectsBagOf, CONTOUR_FX_STEP_ID } from './capabilityMatrix.js';
import {
  renderContourOutline, getContourOutlineStats,
  // Multi-ring outlines (Depth Everywhere N3): the native Outline effect's
  // outer rings (2..4) — predicate + post-closing render pass + the extra
  // geometry reach the ring stack needs (all rings=1-inert by construction).
  outlineRingsWanted, renderOutlineOuterRings, ringsExtraReachU,
} from './fxContourOutline.js';
import { getRenderCaps, BAKE_STAND_CAPS, _resetRenderCapsForTest } from '../renderCaps.js';
// Sticker v3 (Depth-Everywhere N1): a PUFFY sticker is a FILTER step whose
// applyAsync hits WebGL (the one-shot slab bake) — it inherits the source
// steps' three seam laws through this predicate: the mid-edit WebGL fence
// (runAsyncUpgrade), the export pre-bake (fxPrimeExport) and the export
// render's primed-stash draw (renderFxStack). No cycle: fxStickerShape
// never imports this module.
import { stickerWantsGlBake, getStickerShapeStats } from './fxStickerShape.js';

// ---------------------------------------------------------------- module state
let FX_DISABLED = false;
let canaryResult = null; // null = not yet run · 'ok' · 'failed: …'
let canaryRan = false;
let exportDepth = 0; // >0 → export render: full quality sync, no stash/schedule

const stats = {
  syncRuns: 0, // filter chains run synchronously (cache redraw / param tick)
  asyncRuns: 0, // full-quality upgrades completed
  lastSyncMs: 0,
  lastAsyncMs: 0,
  attaches: 0,
  detaches: 0,
  errors: 0,
  // kind:'source' instrumentation — probes assert "no WebGL while editing"
  // against sourceBakes, and export reports ride lastSourceMs.
  sourceBakes: 0, // source applyAsync invocations (each one may hit WebGL)
  sourceErrors: 0, // source bakes that threw (flat fallback stashed)
  lastSourceMs: 0,
  exportPrimes: 0, // fxPrimeExport bakes completed
  // die-cut closing instrumentation — probe-diecut asserts cost and coverage
  closeRuns: 0, // closings that actually ran (qualifying stroke, r >= 1.5px)
  lastCloseMs: 0,
  lastCloseBridged: 0, // bridged px of the most recent run (0 = clean shape)
  // viewport-patch instrumentation (probe-viewpatch asserts cadence + fence)
  patchBakes: 0, // viewport patches that LANDED (source or filter)
  patchDrops: 0, // standing patches dropped (zoom-out, off-viewport, edit)
  patchEvals: 0, // debounced evaluator passes (diagnosis: is the chain alive?)
  patchNotifies: 0, // fxNotifyViewportChange calls that ARMED the debounce
  lastPatchMs: 0,
  // stash-pool instrumentation (probe-schoolpc asserts budget + eviction laws)
  poolChecks: 0, // governance passes that actually accounted the graph
  poolEvictions: 0, // objects whose live fx bitmaps were freed by governance
};

const loggedOnce = new Set();
function logOnce(key, err) {
  stats.errors += 1;
  if (loggedOnce.has(key)) return;
  loggedOnce.add(key);
  console.error(`[fx] ${key}`, err || '');
}

/** No-silent-caps surface: an honest, non-error, once-per-key console note
 * for every place a runtime cap delivers fewer pixels than needed. Not
 * stats.errors (nothing failed — a documented ceiling bound). */
const warnedOnce = new Set();
function warnOnce(key, msg) {
  if (warnedOnce.has(key)) return;
  warnedOnce.add(key);
  console.warn(`[fx] ${msg}`);
}

// ---------------------------------------------------------------- scratch pool
// Transient per-render canvases (sync path runs per keystroke — no per-frame
// allocation). Size-bucketed to 256px so a growing textbox reuses its canvas;
// giant one-offs (export renders) are never pooled.
const POOL = [];
const POOL_MAX = 3;
const POOL_MAX_AREA = 8 * 1024 * 1024; // don't retain >8Mpx canvases

function bucket(n) {
  return Math.max(256, Math.ceil(n / 256) * 256);
}

function acquireScratch(w, h, pooled) {
  const bw = bucket(w);
  const bh = bucket(h);
  if (pooled) {
    for (let i = 0; i < POOL.length; i++) {
      const e = POOL[i];
      if (e.canvas.width >= bw && e.canvas.height >= bh
        && e.canvas.width * e.canvas.height <= bw * bh * 4) {
        POOL.splice(i, 1);
        e.ctx.setTransform(1, 0, 0, 1, 0, 0);
        e.ctx.filter = 'none';
        e.ctx.globalAlpha = 1;
        e.ctx.globalCompositeOperation = 'source-over';
        e.ctx.clearRect(0, 0, e.canvas.width, e.canvas.height);
        return e;
      }
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = pooled ? bw : Math.max(1, Math.ceil(w));
  canvas.height = pooled ? bh : Math.max(1, Math.ceil(h));
  // Filters read pixels back every run — keep these canvases CPU-side.
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  return { canvas, ctx };
}

function releaseScratch(entry) {
  if (!entry) return;
  if (entry.canvas.width * entry.canvas.height > POOL_MAX_AREA) return; // drop giants
  POOL.push(entry);
  while (POOL.length > POOL_MAX) POOL.shift();
}

/** Per-render lease: effects borrow scratches through env.getScratch and the
 * seam returns everything to the pool in one finally — an effect can never
 * leak a canvas by forgetting to release. */
function makeLease(pooled) {
  const held = [];
  return {
    get(w, h) {
      const e = acquireScratch(w, h, pooled);
      if (pooled) held.push(e);
      return e;
    },
    releaseAll() {
      held.forEach(releaseScratch);
      held.length = 0;
    },
  };
}

// ---------------------------------------------------------------- geometry
/** Recipe unit in object units: fontSize/100 for text (matches the emboss/
 * effectDefs convention), min(w,h)/100 for anything else. */
function unitFor(obj, w, h) {
  return obj.fontSize ? obj.fontSize / 100 : Math.max(0.01, Math.min(w, h) / 100);
}

/** True when any step is a registered kind:'source' effect (raster
 * replacement — WebGL bake). Unknown ids never count. */
function hasSourceStep(steps) {
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (def && def.kind === 'source') return true;
  }
  return false;
}

/** True when a source step is present AND non-empty (it will actually replace
 * the raster). An empty source step (hand-edited JSON) leaves the 2D base on
 * screen, so everything keyed off "the raster is a bake" — the hash's
 * highlight/styles exclusions, the live highlight underlay — must use THIS,
 * not hasSourceStep: keying off mere presence would drop textBackgroundColor
 * from the hash while the 2D base (which paints it) is still the raster. */
function hasActiveSourceStep(steps) {
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (!def || def.kind !== 'source') continue;
    try {
      if (!def.isEmpty(def.sanitize(step.params || {}))) return true;
    } catch (_) { /* sanitize threw — treat as inactive */ }
  }
  return false;
}

/** Filter ids CONSUMED by an active source step in this stack (composition
 * contract — see fxRegistry's `consumes` notes): the source renders those
 * filters' look itself (extrude maps the bevel step's depth onto geometric
 * edge rounding), so the seam must not run them — not in the sync chain,
 * not in the full chain, not in the margins — while their steps stay in
 * data.fx untouched. An EMPTY source step (depth 0 wouldn't normally
 * persist, but hand-edited JSON can carry one) consumes nothing, so its
 * filters run live exactly as without it. Returns null when nothing is
 * consumed (the common case — zero Set cost per render). */
function consumedIdsOf(steps) {
  let out = null;
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (!def || def.kind !== 'source' || !def.consumes || !def.consumes.length) continue;
    try {
      if (def.isEmpty(def.sanitize(step.params || {}))) continue;
    } catch (e) {
      logOnce(`consumes(${step.id}) sanitize threw`, e);
      continue;
    }
    for (const id of def.consumes) (out || (out = new Set())).add(id);
  }
  return out;
}

/** Combined per-axis render margins, object units. Filters contribute their
 * scalar spill on both axes; source steps contribute their declared
 * anisotropic bounds (rotation/perspective grow x and y differently).
 * Consumed filters (an active source step renders their look — see
 * consumedIdsOf) contribute nothing: they never paint here. Unknown ids (a
 * design saved by a future version) contribute nothing and render
 * base-only. */
function marginsOf(obj, steps, u, w, h) {
  let mx = 0;
  let my = 0;
  const consumed = consumedIdsOf(steps);
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (!def) continue;
    if (def.kind !== 'source' && consumed && consumed.has(step.id)) continue;
    try {
      const params = def.sanitize(step.params || {});
      if (def.kind === 'source') {
        // geo.obj (additive, 3D-controls lane): source steps that re-express
        // NATIVE-prop effects in the bake (extrude's outline rim reads
        // obj.strokeWidth) need the object to size their margins — params
        // alone can't see native props. Read-only, may be undefined for
        // detached callers.
        const b = def.bounds(params, { u, w, h, obj }) || {};
        mx += Math.max(0, b.marginX || 0);
        my += Math.max(0, b.marginY || 0);
      } else if (typeof def.spillXY === 'function') {
        // Anisotropic FILTER margins (sticker-shapes lane): a badge's
        // overhang depends on the box aspect (a circle around a wide word
        // reaches far past the top/bottom), which the scalar spill cannot
        // express. Same {u,w,h,obj} geometry the source bounds get; scalar
        // spill remains the patch-bake guard (patchGuardU) for these steps.
        const s = def.spillXY(params, { u, w, h, obj }) || {};
        mx += Math.max(0, s.marginX || 0);
        my += Math.max(0, s.marginY || 0);
      } else {
        const s = Math.max(0, def.spill(params, u) || 0);
        mx += s;
        my += s;
      }
    } catch (e) {
      logOnce(`bounds(${step.id}) threw`, e);
    }
  }
  return { mx, my };
}

/** Object-unit render bounds. Mirrors _getCacheCanvasDimensions' math
 * (transformed dims without skew, divided back by scale) + the glyph-overhang
 * margin fabric's Text cache adds (fontSize/2 per side) + the fx spill.
 * Curved text (text-on-path) lays glyphs along the PATH's bbox, far outside
 * the wrap box — grow to the path bounds + a full fontSize of glyph reach. */
function fxGeometry(obj, steps) {
  const dim = obj._getTransformedDimensions({ skewX: 0, skewY: 0 });
  const w = dim.x / (obj.scaleX || 1);
  const h = dim.y / (obj.scaleY || 1);
  const u = unitFor(obj, w, h);
  const { mx, my } = marginsOf(obj, steps, u, w, h);
  const fs = obj.fontSize || 0;
  let halfW = w / 2 + fs / 2;
  let halfH = h / 2 + fs / 2;
  const path = obj.path;
  if (path && typeof path.width === 'number') {
    halfW = Math.max(halfW, path.width / 2 + fs);
    halfH = Math.max(halfH, path.height / 2 + fs);
  }
  halfW += mx;
  halfH += my;
  // Multi-ring outlines (N3): a text band's outer rings reach past the
  // fontSize/2 glyph pad + stroke reach — grow the render box here, ONCE for
  // every caller (fx stack, plain lanes, export prime), so offscreen bakes
  // and exports never clip the ring stack. Exactly 0 unless the object's
  // stroke-slot entry carries rings > 1 (the parity law); shape/icon rings
  // ride the contourBand step's own spill() instead.
  const ringR = ringsExtraReachU(obj);
  if (ringR > 0) {
    halfW += ringR;
    halfH += ringR;
  }
  return { w, h, u, mx, my, halfW: Math.max(1, halfW), halfH: Math.max(1, halfH) };
}

// ---------------------------------------------------------------- hashing
/** Signature of a fabric paint (solid / Gradient / Pattern) for the stash key. */
function paintSig(p) {
  if (p == null) return 'n';
  if (typeof p === 'string') return p;
  if (p.colorStops) {
    try {
      return `g${p.type}${JSON.stringify(p.colorStops)}${JSON.stringify(p.coords || '')}`;
    } catch (_) { return 'g?'; }
  }
  if (p.source) {
    const src = p.source.src || p.source.currentSrc || '';
    return `p${src.length}:${src.slice(0, 48)}`;
  }
  return 'o';
}

/** Stash key: fx recipe + everything that changes the BASE raster + a scale
 * bucket (0.25 steps — zooming within a bucket reuses the stash, crossing one
 * re-renders). data.fx params come out of sanitize with a fixed key order, so
 * plain JSON.stringify is stable. A base-affecting prop missing from this list
 * would leave a stale stash on screen until any listed prop changes — prefer
 * over-inclusion. */
// SOURCED stacks (an ACTIVE source step — the raster is a 3D bake) hash
// differently in two spots, both deliberate:
//   - textBackgroundColor is EXCLUDED: the highlight is drawn live by the
//     seam as a 2D underlay behind the bake (zero bake cost — see
//     drawSourcedUnderlays), so it must never force a WebGL re-bake;
//   - obj.styles collapses to the keys the bake actually RENDERS
//     (bakedStylesSig). Since the GlyphTrace lane (2026-07-20) that is the
//     per-char fills AND the font-affecting keys — fontFamily/fontWeight/
//     fontStyle/fontSize/deltaY now change the traced 3D letterforms and
//     layout, so they must re-bake; the still-2D-only keys (per-char
//     underline/background/stroke) stay excluded so they never force a
//     WebGL re-bake for nothing.
// data.textFill (the fill RECIPE) rides the hash for EVERY stack: a texture's
// pattern-size change alters neither paintSig (same source image) nor any
// other listed prop, yet changes both the 2D raster and the 3D face repeat.
const BAKED_STYLE_KEYS = ['fill', 'fontFamily', 'fontWeight', 'fontStyle', 'fontSize', 'deltaY'];

function bakedStylesSig(styles) {
  let out = '';
  try {
    for (const li of Object.keys(styles)) {
      const line = styles[li];
      if (!line) continue;
      for (const ci of Object.keys(line)) {
        const decl = line[ci];
        if (!decl) continue;
        let sig = '';
        for (const key of BAKED_STYLE_KEYS) {
          const v = decl[key];
          if (v === undefined) continue;
          sig += `${key}=${typeof v === 'string' || typeof v === 'number' ? v : 'o'},`;
        }
        if (sig) out += `${li}:${ci}:${sig};`;
      }
    }
  } catch (_) { return '?'; }
  return out;
}

function fxHashOf(obj, steps, scale) {
  const baked = hasActiveSourceStep(steps);
  let stylesSig = '';
  if (obj.styles) {
    if (baked) {
      stylesSig = bakedStylesSig(obj.styles);
    } else {
      try { stylesSig = JSON.stringify(obj.styles); } catch (_) { stylesSig = '?'; }
    }
  }
  let stepsSig = '';
  try { stepsSig = JSON.stringify(steps); } catch (_) { stepsSig = String(steps.length); }
  let textFillSig = '';
  if (obj.data && obj.data.textFill != null) {
    try { textFillSig = JSON.stringify(obj.data.textFill); } catch (_) { textFillSig = '?'; }
  }
  // Poster light (2026-07-19): a DOCUMENT-level input, hashed only for stacks
  // that opt in (any step with lightMode 'poster'). Editing the shared sun
  // then re-renders/re-bakes exactly the opted-in objects through the
  // existing stash-miss machinery — no bespoke walkers — while custom-mode
  // stacks keep an unchanged hash (and their stash). The lightMode FLAG
  // itself already rides stepsSig; this appends the light's VALUES.
  let posterSig = '';
  if (stepsUsePosterLight(steps)) {
    const pl = getPosterLightFor(obj);
    posterSig = `pl${pl.angle}:${pl.elevation}`;
  }
  return [
    stepsSig,
    posterSig,
    obj.text ?? '', obj.fontFamily ?? '', obj.fontWeight ?? '', obj.fontStyle ?? '',
    obj.fontSize ?? '', obj.lineHeight ?? '', obj.charSpacing ?? '', obj.textAlign ?? '',
    obj.underline ? 1 : 0, obj.linethrough ? 1 : 0, obj.overline ? 1 : 0,
    baked ? '' : obj.textBackgroundColor ?? '', obj.direction ?? '',
    Math.round(obj.width || 0), Math.round(obj.height || 0),
    obj.rx || 0, obj.ry || 0, obj.radius || 0,
    paintSig(obj.fill), paintSig(obj.stroke), obj.strokeWidth || 0, obj.paintFirst ?? '',
    // strokeDashArray (union-contour lane, 2026-07-20): Trace's dash/gap
    // sliders change ONLY this prop — it was missing from the hash, so a
    // dash edit under any fx stack kept serving the stale stash (pre-existing
    // gap, now also a contour-band input). Over-inclusion is the documented
    // preference for this list.
    Array.isArray(obj.strokeDashArray) && obj.strokeDashArray.length
      ? obj.strokeDashArray.join('_') : '',
    obj.curveAmount || 0, obj.path ? 1 : 0, obj.pathStartOffset || 0, obj.pathSide ?? '',
    stylesSig, textFillSig,
    scaleBucketOf(scale),
  ].join('');
}

/** Scale bucket: CEIL to 0.25 steps (resolution lane, 2026-07-17). Was
 * Math.round — a stash baked at bucket-low could then serve bucket-high
 * (up to ~28% upscale inside one bucket: baked at 0.88, drawn at 1.12).
 * With ceil, the async bake renders AT the bucket top (scaleBucketTop), so
 * every scale inside the bucket draws a stash at >= its own resolution —
 * never upscaled, at worst ~25% oversampled (cheap, GC'd with the stash). */
function scaleBucketOf(scale) {
  return Math.ceil(scale / 0.25 - 1e-9);
}

function scaleBucketTop(scale) {
  return scaleBucketOf(scale) * 0.25;
}

/** Scale-free hash for export stashes: the export render's device scale is
 * the print multiplier (unknowable when the screen stash was cut), so the
 * pre-pass keys its bake by content only (scale bucket pinned to 0). */
function fxBaseHashOf(obj, steps) {
  return fxHashOf(obj, steps, 0);
}

// ---------------------------------------------------------------- render core
/**
 * Resolution ceilings for the CURRENT render mode. Runtime-probed device
 * limits (lib/renderCaps) intersected with fabric's config so an environment
 * that never raised config (unit canaries, embedded canvases) keeps its old
 * conservative behavior; the editor raises config to the runtime caps at
 * boot (useCanvasEditor) and the export bracket raises it to the transient
 * export budgets (exportUtils). The legacy shape (min side 2048 / min area
 * 16.7Mpx) is preserved as the floor.
 */
function renderBoundsNow() {
  const caps = getRenderCaps();
  const side = exportDepth > 0 ? caps.exportSide : caps.side;
  const area = exportDepth > 0 ? caps.exportArea : caps.area;
  return {
    maxSide: Math.max(2048, Math.min(side, config.maxCacheSideLimit || side)),
    maxArea: Math.max(2048 * 2048 * 4, Math.min(area, (config.perfLimitSizeTotal || area / 4) * 4)),
  };
}

/**
 * True when a stash was BAKED FOR less resolution than this draw needs
 * (>5% under). Keyed off the recorded request scale (reqScale), NOT the
 * delivered pixels: a bake that was requested at full scale but delivered
 * less hit a runtime cap — re-requesting would re-deliver the same, so it
 * is final, not stale. Covers the joystick's {scaleMul:0.6} drag bakes
 * (self-expire after the drag), a lost release-bake race, and stashes cut
 * before a cap/config raise. Gated off mid-drag: the joystick owns bake
 * cadence while __fxDragTilt exists.
 */
function stashUnderRes(obj, st, scale) {
  if (obj.__fxDragTilt) return false;
  return (st.reqScale || Infinity) < scale * 0.95;
}

/** ax/ay: object-unit anchor shift (source steps) — the canvas's center lands
 * at (-ax, -ay) so the step's anchor point sits on the object's origin. */
function drawResultOnCtx(ctx, canvas, W, H, halfW, halfH, ax = 0, ay = 0) {
  ctx.drawImage(canvas, 0, 0, W, H, -halfW - ax, -halfH - ay, halfW * 2, halfH * 2);
}

/** Draw a stash. While the 3D joystick is mid-drag it parks the tilt delta
 * since the stash's bake on __fxDragTilt {drx, dry} (degrees) — a cheap
 * trackball read on the flat raster (yaw compresses x, pitch compresses y,
 * small shears sell the turn) keeps the pad feeling live between the
 * throttled real bakes, which land and zero the delta. Approximate on
 * purpose: pixels only, never serialized, gone on release. */
function drawStashOnCtx(obj, ctx, st) {
  const tilt = obj.__fxDragTilt;
  if (tilt && (tilt.drx || tilt.dry)) {
    const yaw = ((tilt.dry || 0) * Math.PI) / 180;
    const pitch = ((tilt.drx || 0) * Math.PI) / 180;
    ctx.save();
    ctx.transform(
      Math.cos(yaw), Math.sin(pitch) * 0.12,
      -Math.sin(yaw) * 0.12, Math.cos(pitch),
      0, 0,
    );
    drawResultOnCtx(ctx, st.canvas, st.W, st.H, st.halfW, st.halfH, st.ax || 0, st.ay || 0);
    ctx.restore();
    return;
  }
  drawResultOnCtx(ctx, st.canvas, st.W, st.H, st.halfW, st.halfH, st.ax || 0, st.ay || 0);
}

/**
 * Live 2D underlays drawn BEHIND a baked (source-step) raster — currently the
 * text highlight: fabric's own per-line background renderer paints the
 * object-level textBackgroundColor AND per-char background styles in object
 * space, exactly the ctx space _render receives, so the highlight reads as a
 * flat 2D label behind the standing 3D letters. Drawn on every stash draw at
 * zero bake cost (the hash excludes textBackgroundColor for baked stacks —
 * see fxHashOf), so the fade slider feels live. Plain rects, matching the 2D
 * highlight look exactly (the house 2D path has no rounded corners either).
 * Drawn OUTSIDE the joystick's mid-drag tilt transform on purpose: the label
 * is paper on the poster, not part of the turning slab.
 * Failure posture: a metrics hiccup skips the underlay, never the render.
 */
function drawSourcedUnderlays(obj, ctx) {
  try {
    if (typeof obj._renderTextLinesBackground !== 'function') return;
    const hasPerChar = typeof obj.styleHas === 'function' && obj.styleHas('textBackgroundColor');
    if (!obj.textBackgroundColor && !hasPerChar) return;
    ctx.save();
    obj._renderTextLinesBackground(ctx);
    ctx.restore();
  } catch (e) {
    logOnce('highlight underlay failed — bake drawn without it', e);
  }
}

/** Render the object's base _render into an offscreen at (up to) scale px per
 * object unit, capped by fabric's own cache config so fx never out-allocates
 * the cache it feeds. Returns the canvas + a ready env for the filter chain.
 *
 * region (viewport-patch lane): an object-unit sub-rect {x0,y0,x1,y1}
 * (center-origin — the same space drawResultOnCtx draws into). The offscreen
 * then spans ONLY that rect at `scale` px/unit — the base render lands with
 * the rect's top-left at canvas (0,0), everything outside clipped. The
 * caller owns any filter-guard margin; die-cut closing reads the whole
 * region canvas (patches are viewport-bounded, the O(ink) window is moot). */
function renderBaseOffscreen(obj, base, geo, scale, quality, lease, region = null) {
  const { maxSide, maxArea } = renderBoundsNow();
  const spanX = region ? region.x1 - region.x0 : geo.halfW * 2;
  const spanY = region ? region.y1 - region.y0 : geo.halfH * 2;
  let W = spanX * scale;
  let H = spanY * scale;
  let k = 1;
  if (W > maxSide) k = maxSide / W;
  if (H * k > maxSide) k = maxSide / H;
  if (W * k * (H * k) > maxArea) k *= Math.sqrt(maxArea / (W * k * H * k));
  W = Math.max(8, Math.round(W * k));
  H = Math.max(8, Math.round(H * k));
  const effX = W / spanX;
  const effY = H / spanY;

  const offs = lease.get(W, H);
  const setBaseTransform = (ctx) => {
    if (region) {
      ctx.setTransform(effX, 0, 0, effY, -region.x0 * effX, -region.y0 * effY);
    } else {
      ctx.setTransform(effX, 0, 0, effY, W / 2, H / 2);
    }
  };
  setBaseTransform(offs.ctx);
  // PLATE MODEL (owner 2026-07-20): a sticker is a SOLID PLATE drawn by the fx
  // 'stickerShape' step, never the object's own stroke ring. When the sticker
  // owns the stroke slot its ring is SUPPRESSED in the base (strokeWidth parked
  // at 0 — the fx plate is the sole sticker paint, no double-paint / texture-
  // anchor mismatch), and the base die-cut closing is skipped (the base is now
  // letters-only; its counters are real geometry — the plate does its own
  // closing on the dilated silhouette). Forward-safe: once the stroke slot is
  // freed and an Outline EFFECT stacks, textEffects.stroke.id === 'outline' and
  // its letter-ink stroke is kept. Shape modes already carry strokeWidth 0, so
  // this is a no-op there.
  // Universal-effects U1: the bag dual-reads (data.effects on shapes/icons,
  // data.textEffects on text), and the ring-park below is TEXT-ONLY — a
  // non-text object's live stroke is the Border tool's CONTENT, never the
  // sticker's ring (non-text sticker derives carry no stroke props at all).
  const objKind = objectKindOf(obj);
  const stickerPlate = effectsBagOf(obj)?.stroke?.id === 'sticker'
    && (objKind === 'text' || objKind == null);
  // UNION-CONTOUR LAW (owner 2026-07-20): hollow/trace (contourOutline def
  // marker) stroke the glyphs' TRUE UNION silhouette, not fabric's raw glyph
  // path (whose overlapping subpaths read as internal seams in a/b/y). The
  // raw stroke is parked out of the base pass exactly like the sticker ring;
  // fxContourOutline then draws the band over the base below. On any contour
  // degrade the base is re-rendered WITH its raw stroke — see the contour
  // block after env is built. (contourOutlineWanted is text-gated itself.)
  const contourBand = !stickerPlate && contourOutlineWanted(obj);
  const swPark = (stickerPlate || contourBand) ? (obj.strokeWidth || 0) : 0;
  if (swPark) obj.strokeWidth = 0;
  try {
    base.call(obj, offs.ctx);
  } finally {
    if (swPark) obj.strokeWidth = swPark;
  }
  offs.ctx.setTransform(1, 0, 0, 1, 0, 0);

  const env = {
    scale: (effX + effY) / 2,
    u: geo.u,
    obj,
    width: W,
    height: H,
    quality,
    params: null, // the seam sets this per step
    getScratch: (w, h) => lease.get(w, h),
    diecutBridged: 0,
    // Mode-aware resolution budget for THIS render (live vs export) — source
    // steps size their bake output and pads by it (print300 lane: an export
    // prime may go to exportSide/exportArea, a live bake never past the live
    // caps).
    renderBounds: { side: maxSide, area: maxArea },
    // Object↔canvas mapping for spatial filters (sticker-shapes lane):
    // canvas px = (objectUnits − x0/y0) · effX/effY. One formula for full
    // AND region renders, so a filter that draws analytic geometry (the
    // sticker badge) lands it exactly where the base transform put the text.
    frame: region
      ? { x0: region.x0, y0: region.y0, effX, effY }
      : { x0: -spanX / 2, y0: -spanY / 2, effX, effY },
    // Render-mode flag (union-contour lane): steps that TIER their working
    // resolution (fxContourOutline's trace caps) need the honest live/export
    // split — quality 'full' alone can't tell a debounced upgrade from a
    // print render.
    exporting: exportDepth > 0,
  };

  // renderVariant HOOK (layer-separation lane, owner 2026-07-20; kinds since
  // the union-contour lane): memoized second base passes on demand. Two
  // kinds, each offered ONLY to its family — every other stack keeps a
  // hook-free env and effects stay on their single-pass path bit-for-bit:
  //
  //   'noStroke' (glyphMaskWanted — sticker in any mode, plain outline;
  //   NEVER hollow/trace, whose stroke IS the letterform): the glyph-only
  //   raster behind the letters-vs-sticker layer split (fxBevel masks its
  //   dome to it; fx 'stickerShape' measures its ink box and band from it).
  //   Mechanics: same transform, same clipped region, the object's
  //   strokeWidth parked at 0 around a synchronous base.call — a direct
  //   property write (never set(): no events, no dirty, restored in
  //   finally). Per-char stroke styles are NOT suppressed (they are content,
  //   not a slot effect; also vanishingly rare under a sticker). No die-cut
  //   closing here: glyph pockets are real geometry for the letters layer.
  //
  //   'filledSolid' (contourOutlineWanted — hollow/trace): the TRUE UNION
  //   letterform alpha for the contour trace. Fill forced to an opaque solid
  //   REGARDLESS of clearsFill/routing (per-char content colors irrelevant —
  //   alpha is the target), stroke suppressed, and rendered via fabric's
  //   _setTextStyles + _renderText so backgrounds (object AND per-char
  //   highlight rows), decorations and the curve-path vector are excluded
  //   STRUCTURALLY — none of them are letterform silhouette. Falls back to a
  //   parked full base.call if the text internals ever drift (backgrounds
  //   then pollute the alpha, but the render never breaks — and the contour
  //   caller degrades on any hiccup anyway).
  //
  // A kind not offered returns undefined — callers guard (fxBevel's
  // `if (v && v.canvas)` already does), which is exactly what keeps hollow/
  // trace on the bevel's classic single-raster path: their band IS the
  // letters' ink, so the bevel domes it directly (see glyphMaskWanted's
  // contour-interplay note in effectDefs).
  const wantMask = glyphMaskWanted(obj);
  // OBJECT-SILHOUETTE VARIANT (universal-effects U1) — the non-text analog
  // of the glyph variants, and the contract U3's extrude source input will
  // consume (documented beside renderVariant in fxRegistry). For shape/icon
  // kinds BOTH variant kinds resolve to ONE memoized SILHOUETTE raster:
  //   shape — the object's own path/geometry rendered with the fill parked
  //     to an opaque solid and the stroke parked off: the TRUE silhouette
  //     regardless of live paint (a transparent-fill or gradient shape
  //     silhouettes identically; the Border tool's stroke is content and is
  //     deliberately NOT part of the silhouette).
  //   icon — a pristine second base pass: the raster's ALPHA IS the
  //     silhouette (the isIcon tint pipeline bakes color into the element,
  //     alpha untouched, so tint never moves the silhouette). A fresh pass,
  //     never a reuse of the chain canvas — filters mutate src in place
  //     (fxBevel's contact shadow adds alpha) and the silhouette must stay
  //     pre-chain.
  // Offered ONLY when a consumer is live: the sticker family (wantMask), a
  // contourBand step (icon/shape Outline-Hollow-Trace), or an 'extrude'
  // source step (universal-effects U3 — silhouetteOf's traced-icon input;
  // shapes cut exact vectors and never call it, but the offer is uniform
  // and lazy: an uncalled variant costs nothing). Every other stack keeps a
  // hook-free env — fxBevel then keeps its classic single-raster path and
  // domes the shape's full content (bevel-on-Rect, proven).
  const objectSilhouette = (objKind === 'shape' || objKind === 'icon')
    && (wantMask || (Array.isArray(obj.data?.fx)
      && obj.data.fx.some((s) => s && (s.id === CONTOUR_FX_STEP_ID || s.id === 'extrude'))));
  if (objectSilhouette) {
    let sil = null;
    env.renderVariant = (kind = 'noStroke') => {
      if (kind !== 'noStroke' && kind !== 'filledSolid') return undefined;
      if (sil) return sil;
      const offs2 = lease.get(W, H);
      setBaseTransform(offs2.ctx);
      const prevFill = obj.fill;
      const prevStroke = obj.stroke;
      const sw = obj.strokeWidth;
      if (objKind === 'shape') {
        // Direct property writes, never set(): no events, no dirty; restored
        // in finally — the same parking mechanics as the glyph variants.
        obj.fill = '#000000';
        obj.stroke = null;
        if (sw) obj.strokeWidth = 0;
      }
      try {
        base.call(obj, offs2.ctx);
      } finally {
        if (objKind === 'shape') {
          obj.fill = prevFill;
          obj.stroke = prevStroke;
          if (sw) obj.strokeWidth = sw;
        }
      }
      offs2.ctx.setTransform(1, 0, 0, 1, 0, 0);
      sil = { canvas: offs2.canvas, W, H };
      return sil;
    };
  } else if (wantMask || contourBand) {
    const variants = {};
    env.renderVariant = (kind = 'noStroke') => {
      if (kind === 'noStroke' && !wantMask) return undefined;
      // 'filledSolid' is offered to the STICKER family too (v3 exact-plate
      // lane): the union letterform alpha is the contour source the exact
      // plate offsets — the same variant, the same tracer, the same cache
      // as hollow/trace (which can never coexist with a sticker: both claim
      // the stroke slot). Bevel behavior is untouched — it only ever asks
      // for 'noStroke'.
      if (kind === 'filledSolid' && !contourBand && !wantMask) return undefined;
      if (kind !== 'noStroke' && kind !== 'filledSolid') return undefined;
      if (variants[kind]) return variants[kind];
      // A sticker plate already rendered the base glyph-only (ring suppressed
      // above), so the base IS the stroke-suppressed variant — reuse it
      // instead of a redundant second pass. Plain outline keeps the real pass
      // (its stroke is letter ink, present in the base).
      if (kind === 'noStroke' && stickerPlate) {
        variants[kind] = { canvas: offs.canvas, W, H };
        return variants[kind];
      }
      const offs2 = lease.get(W, H);
      setBaseTransform(offs2.ctx);
      const sw = obj.strokeWidth;
      const parkFill = kind === 'filledSolid';
      const prevFill = obj.fill;
      const prevStroke = obj.stroke;
      if (sw) obj.strokeWidth = 0;
      if (parkFill) {
        obj.fill = '#000000';
        obj.stroke = null;
      }
      try {
        if (parkFill && typeof obj._setTextStyles === 'function'
          && typeof obj._renderText === 'function') {
          obj._setTextStyles(offs2.ctx);
          obj._renderText(offs2.ctx);
        } else {
          base.call(obj, offs2.ctx);
        }
      } finally {
        if (sw) obj.strokeWidth = sw;
        if (parkFill) {
          obj.fill = prevFill;
          obj.stroke = prevStroke;
        }
      }
      offs2.ctx.setTransform(1, 0, 0, 1, 0, 0);
      variants[kind] = { canvas: offs2.canvas, W, H };
      return variants[kind];
    };
  }

  // UNION-CONTOUR BAND (owner 2026-07-20): the base above rendered hollow/
  // trace with the raw stroke parked out — draw the union-silhouette band
  // over it now (fxContourOutline: filledSolid variant → binary alpha →
  // marching-squares walk → DP → one stroke through the object's live stroke
  // props). On ANY degrade (blank text, region bake without remembered
  // contours, internal hiccup) the base is re-rendered WITH its raw stroke —
  // the letters must never vanish; the seams of the raw path are the honest
  // fallback look. env.contourBanded is the plain-path caller's signal
  // (renderContourPlain keeps the stock vector render on false).
  env.contourBanded = false;
  if (contourBand) {
    let banded = false;
    try {
      banded = renderContourOutline(offs.ctx, env);
    } catch (e) {
      logOnce('contour band failed — raw-path stroke drawn', e);
    }
    if (banded) {
      env.contourBanded = true;
    } else {
      offs.ctx.save();
      offs.ctx.setTransform(1, 0, 0, 1, 0, 0);
      offs.ctx.clearRect(0, 0, W, H);
      offs.ctx.restore();
      setBaseTransform(offs.ctx);
      try {
        base.call(obj, offs.ctx);
      } finally {
        offs.ctx.setTransform(1, 0, 0, 1, 0, 0);
      }
    }
  }

  // Die-cut closing — BEFORE any filter step, so the bevel's height field
  // sees the bridged silhouette and domes over it (the "pocket above the i"
  // becomes a smooth crown). Applies whenever the object's stroke-slot effect
  // is a continuous border band (diecutStrokeReach > 0 — sticker/outline
  // family, never hollow/trace); costs nothing for every other object.
  try {
    // Skip for a sticker plate: its ring is suppressed above (the base is
    // letters-only — closing would fill the glyphs' own counters), and the
    // plate does its die-cut closing on its dilated silhouette instead.
    const reachU = stickerPlate ? 0 : diecutStrokeReach(obj);
    if (reachU > 0) {
      const r = diecutCloseRadiusPx(reachU * env.scale, geo.u * env.scale);
      if (r > 0) {
        const t0 = performance.now();
        // Read only where base INK can live: the glyph box + fabric's own
        // glyph-overhang pad + the stroke reach. The fx spill margins (mx/my)
        // only receive pixels from LATER filter steps — skipping them keeps
        // the readback O(ink), not O(canvas). Region renders read the whole
        // (viewport-bounded) canvas — ink can live anywhere in the slice.
        const inkHalfW = Math.min(geo.halfW, geo.halfW - geo.mx + reachU);
        const inkHalfH = Math.min(geo.halfH, geo.halfH - geo.my + reachU);
        const rx0 = region ? 0 : Math.max(0, Math.floor(W / 2 - inkHalfW * effX) - r - 2);
        const ry0 = region ? 0 : Math.max(0, Math.floor(H / 2 - inkHalfH * effY) - r - 2);
        const rx1 = region ? W : Math.min(W, Math.ceil(W / 2 + inkHalfW * effX) + r + 2);
        const ry1 = region ? H : Math.min(H, Math.ceil(H / 2 + inkHalfH * effY) + r + 2);
        const bwR = rx1 - rx0;
        const bhR = ry1 - ry0;
        if (bwR > 2 && bhR > 2) {
          const img = offs.ctx.getImageData(rx0, ry0, bwR, bhR);
          const n = bridgeDiecutPockets(img, bwR, bhR, r);
          if (n > 0) offs.ctx.putImageData(img, rx0, ry0);
          env.diecutBridged = n;
          stats.closeRuns += 1;
          stats.lastCloseMs = performance.now() - t0;
          stats.lastCloseBridged = n;
        }
      }
    }
  } catch (e) {
    logOnce('diecut closing failed — pocketed base rendered', e);
  }

  // MULTI-RING OUTLINES (Depth Everywhere N3): the native Outline effect's
  // outer rings (2..4) paint AFTER the die-cut closing — the closing must
  // never read ring gaps as pockets to bridge — and before the filter chain
  // (a bevel domes ring ink like any band). Ring 1 is the native stroke
  // already in the base, untouched: rings=1 recipes cannot reach this block
  // (outlineRingsWanted gates on rings > 1), so the pre-lane render is
  // byte-identical. Hollow/trace rings ride renderContourOutline above;
  // pending/unavailable geometry honestly leaves the native band alone.
  env.outlineRinged = false;
  if (!stickerPlate && !contourBand && outlineRingsWanted(obj)) {
    try {
      env.outlineRinged = renderOutlineOuterRings(offs.ctx, env);
    } catch (e) {
      logOnce('outline outer rings failed — native band kept', e);
    }
  }
  return { canvas: offs.canvas, W, H, env };
}

/** Synchronous filter chain, declared order. Unknown ids (a design saved by a
 * future version) contribute nothing — the base renders untouched. Filters
 * consumed by an active source step are skipped here too: the sync pass may
 * be painting the interim flat base while the 3D bakes, and a 2D bevel that
 * flashes on it would then vanish when the bake (which owns that look)
 * lands. */
function runChainSync(canvas, steps, env) {
  let cur = canvas;
  const consumed = consumedIdsOf(steps);
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (!def || typeof def.applySync !== 'function') continue;
    if (consumed && consumed.has(step.id)) continue;
    const params = def.sanitize(step.params || {});
    if (def.isEmpty(params)) continue;
    env.params = params;
    cur = def.applySync(cur, env) || cur;
  }
  return cur;
}

/** True while any handle drag (scale/rotate/move) is live on the object's
 * canvas. Volume-carrying steps must never rebuild mid-transform: their
 * geometry (Poisson/levelSet WASM) is SYNCHRONOUS CPU in the 100-700ms
 * class, the drag re-keys the bake every debounce tick, and back-to-back
 * solves saturate the main thread — the owner-reported resize FREEZE
 * (2026-07-22). The old stash keeps drawing scaled during the gesture
 * (exactly what the stale-stash branch already does); one build lands on
 * release via the re-armed timer below. */
function transformLive(obj) {
  return !!(obj.canvas && obj.canvas._currentTransform);
}
function stepsHaveVolume(steps) {
  if (!Array.isArray(steps)) return false;
  return steps.some((s) => {
    if (!s || !s.params) return false;
    const v = s.params.volumeStyle;
    return v === 'puffy' || v === 'pillow' || s.params.body === 'puffy';
  });
}

/**
 * VOLUME SWAP LANDING (Depth Everywhere lane VW): the worker pool's
 * full-density mesh just parked on the object's channel — re-bake through
 * the STANDARD upgrade path so the stash swaps in place. Laws:
 *   - force: the standing stash has the SAME hash (params unchanged) at
 *     adequate reqScale — only force replaces it; the stash's
 *     volumeQuality marker flips 'preview' → 'full'.
 *   - no loop: the re-bake's volume build HITS the client's landed cache
 *     (same signature — deterministic ring extraction), so it posts no new
 *     job and the landing callback can never re-trigger itself; the
 *     client's ping-pong breaker backstops even a determinism bug.
 *   - every fence holds: mid-gesture (transform), mid-edit, mid-build
 *     (single-flight) and the 600ms rate window all re-arm the tick
 *     instead of baking — the swap waits its turn, pixels only.
 *   - settled states stay settled: the tick rides its OWN timer prop
 *     (__fxSwapTimer, cleared on detach), never __fxTimer — probe waits
 *     that poll "stash present + no debounce pending" see the preview
 *     stash as a legitimate settled state, then the swap replaces it with
 *     another settled state under the same hash.
 */
function scheduleVolumeSwap(obj) {
  if (FX_DISABLED || exportDepth > 0) return;
  const tick = () => {
    obj.__fxSwapTimer = null;
    if (!obj.__fxAttached || !obj.canvas) return;
    const steps = obj.data && obj.data.fx;
    if (!Array.isArray(steps) || !stepsHaveVolume(steps)) return;
    if (obj.isEditing || transformLive(obj) || obj.__fxVolumeBusy
      || performance.now() - (obj.__fxVolumeLastEnd || 0) < 600) {
      obj.__fxSwapTimer = setTimeout(tick, 250);
      return;
    }
    runAsyncUpgrade(obj, { force: true }).catch((e) => logOnce('volume swap bake failed', e));
  };
  if (obj.__fxSwapTimer) clearTimeout(obj.__fxSwapTimer);
  obj.__fxSwapTimer = setTimeout(tick, 30);
}

/** Debounced full-quality upgrade. Pixels only: results land in the instance
 * stash, never in JSON or history. Superseded runs (newer edits, detach,
 * removal from canvas) are dropped by generation/hash checks. */
function scheduleAsync(obj) {
  if (exportDepth > 0) return;
  if (obj.__fxTimer) clearTimeout(obj.__fxTimer);
  obj.__fxTimer = setTimeout(() => {
    obj.__fxTimer = null;
    const steps = obj.data && obj.data.fx;
    // Volume gesture fence: mid-transform, mid-build (single-flight — the
    // WASM solve can outlast the debounce), or inside the RATE LIMIT window
    // (size-field scrubs and other non-transform gestures re-key the bake
    // per tick; one bounded build per window keeps the thread breathing),
    // re-arm instead of building. Self-resolves on release/completion.
    if (stepsHaveVolume(steps) && (transformLive(obj) || obj.__fxVolumeBusy
      || performance.now() - (obj.__fxVolumeLastEnd || 0) < 600)) {
      scheduleAsync(obj);
      return;
    }
    runAsyncUpgrade(obj).catch((e) => logOnce('async upgrade failed', e));
  }, 150);
}

/**
 * Full-quality chain at `scale` device px per object unit — the shared core
 * of the debounced upgrade AND the export pre-pass. Declared order:
 * applyAsync when a step has one, else applySync at quality 'full'. Source
 * steps REPLACE the working raster and re-map it in object units; a throwing
 * source step keeps the flat base (logged once, stashed anyway so the render
 * stays stable instead of hammering WebGL every frame). Filters consumed by
 * an active source step never run — the source owns their look (the 2D
 * bevel must not ALSO shade the 3D raster it was mapped into).
 */
async function runChainFull(obj, steps, geo, scale, lease) {
  const base = obj.__fxBase;
  if (!base) return null;
  const rendered = renderBaseOffscreen(obj, base, geo, scale, 'full', lease);
  const { env } = rendered;
  // The REQUESTED device scale, before renderBaseOffscreen's caps. Source
  // steps re-cut their raster from vectors (they replace the base, they do
  // not resample it), so their bake must target THIS — env.scale can be
  // lower when the margin-padded BASE raster hit a side/area cap that the
  // tighter final frame would not hit (measured: a DPR-2 zoom-2.5 plate
  // frame delivered 0.90× because the base's tall margins capped first).
  env.reqScale = scale;
  // Lane VW: the volume worker's landing callback — a source step passes it
  // into its bake (fxExtrude.volumeUpgrade); when the full-density mesh
  // lands, scheduleVolumeSwap force-rebakes through this same path and the
  // stash swaps in place. Whole-object bakes only (runChainPatch offers no
  // hook, so patch bakes never post swap jobs — the fxExtrude notes).
  env.scheduleSourceRefresh = () => scheduleVolumeSwap(obj);
  let cur = rendered.canvas;
  let W = rendered.W;
  let H = rendered.H;
  let volumeQuality = null; // lane VW: the stash's swap marker
  // Base-raster mapping (filters keep it): canvas spans [±halfW, ±halfH].
  let map = { halfW: geo.halfW, halfH: geo.halfH, ax: 0, ay: 0 };
  const consumed = consumedIdsOf(steps);
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (!def) continue;
    if (def.kind !== 'source' && consumed && consumed.has(step.id)) continue;
    const params = def.sanitize(step.params || {});
    if (def.isEmpty(params)) continue;
    env.params = params;
    if (def.kind === 'source') {
      if (typeof def.applyAsync !== 'function') continue;
      stats.sourceBakes += 1;
      const tS = performance.now();
      try {
        const out = await def.applyAsync(cur, env);
        if (out && out.canvas) {
          cur = out.canvas;
          W = out.canvas.width;
          H = out.canvas.height;
          map = {
            halfW: Math.max(0.5, out.halfW || geo.halfW),
            halfH: Math.max(0.5, out.halfH || geo.halfH),
            ax: out.ax || 0,
            ay: out.ay || 0,
          };
          if (out.volumeQuality) volumeQuality = out.volumeQuality;
          // Re-point env at the replacement so later filter steps shade IT.
          env.width = W;
          env.height = H;
          env.scale = W / (map.halfW * 2);
        }
      } catch (e) {
        stats.sourceErrors += 1;
        logOnce(`source(${step.id}) bake failed — flat fallback`, e);
      }
      stats.lastSourceMs = performance.now() - tS;
    } else if (typeof def.applyAsync === 'function') {
      cur = (await def.applyAsync(cur, env)) || cur;
    } else if (typeof def.applySync === 'function') {
      cur = def.applySync(cur, env) || cur;
    }
  }
  return { canvas: cur, W, H, volumeQuality, ...map };
}

/**
 * @param {object} [opts]
 *   scaleMul   bake-resolution multiplier (<1 = cheaper bake); the stash is
 *              keyed at the DRAW scale regardless, so a low-res drag bake
 *              still hits (soft for a beat — the release bake replaces it).
 *   allowStale land the result even when the params moved on mid-bake (the
 *              joystick's throttled drag bakes: a slightly-stale 3D beats a
 *              flat flash; the stash stays keyed by ITS OWN bake-time hash,
 *              so the next render sees the mismatch and schedules the
 *              corrective bake — convergence in one extra hop).
 *   force      skip the fresh-stash early-out (the release bake must replace
 *              a low-res drag stash that carries the same hash).
 */
async function runAsyncUpgrade(obj, { scaleMul = 1, allowStale = false, force = false } = {}) {
  if (FX_DISABLED || !obj.__fxAttached || !obj.canvas) return;
  const steps = obj.data && obj.data.fx;
  if (!Array.isArray(steps) || steps.length === 0) return;
  // A source step must NEVER touch WebGL mid-edit; the exit re-render's
  // stash miss re-schedules this, so the 3D returns right after editing.
  // A PUFFY sticker (filter whose applyAsync bakes the slab) inherits the
  // same fence — mid-edit renders stay on the flat-dome sync interim and
  // the stash keeps its pre-edit hash, so the exit render misses and the
  // slab returns right after editing (sticker v3 lane).
  if ((hasSourceStep(steps) || stickerWantsGlBake(steps)) && obj.isEditing) return;
  // Volume single-flight marker (see scheduleAsync's gesture fence): the
  // synchronous WASM solve must never stack. try/finally below clears it.
  const volumeRun = stepsHaveVolume(steps);
  if (volumeRun) {
    if (obj.__fxVolumeBusy) { scheduleAsync(obj); return; }
    obj.__fxVolumeBusy = true;
  }
  const gen = (obj.__fxGen = (obj.__fxGen || 0) + 1);
  const scale = obj.__fxLastScale || 1;
  const t0 = performance.now();

  const geo = fxGeometry(obj, steps);
  const hash = fxHashOf(obj, steps, scale);
  // Nothing to do: the stash already holds this exact state AT sufficient
  // resolution (a debounced schedule that fired after an explicit
  // fxRequestUpgrade landed). A same-hash but under-res stash (low-res drag
  // bake that lost the release race) falls through and gets replaced.
  if (!force && obj.__fxStash && obj.__fxStash.hash === hash && obj.__fxStash.canvas
    && !stashUnderRes(obj, obj.__fxStash, scale)) {
    if (volumeRun) obj.__fxVolumeBusy = false; // early exit — release the single-flight
    return;
  }
  // Render at the BUCKET TOP, never below the exact scale: every draw scale
  // inside this hash bucket then reads the stash at >= its own resolution
  // (see scaleBucketOf). reqScale records what this bake aimed for — the
  // under-res self-expiry (stashUnderRes) keys off it.
  const reqScale = Math.max(0.02, scaleBucketTop(scale) * scaleMul);
  const lease = makeLease(false); // async is rare — fresh canvases, stash owns the result
  obj.__fxBakeBusy = (obj.__fxBakeBusy || 0) + 1; // pool: mid-bake, never evict
  let res;
  try {
    res = await runChainFull(obj, steps, geo, reqScale, lease);
  } finally {
    obj.__fxBakeBusy = Math.max(0, (obj.__fxBakeBusy || 1) - 1);
    if (volumeRun) {
      obj.__fxVolumeBusy = false;
      obj.__fxVolumeLastEnd = performance.now(); // rate-limit window anchor
    }
  }
  if (!res) return;
  if (gen !== obj.__fxGen || !obj.__fxAttached) return; // superseded mid-flight
  if (!allowStale && fxHashOf(obj, obj.data?.fx || [], obj.__fxLastScale || 1) !== hash) return; // stale
  if (res.W * res.H <= renderBoundsNow().maxArea) {
    obj.__fxStash = {
      hash,
      baseHash: fxBaseHashOf(obj, steps),
      canvas: res.canvas,
      W: res.W,
      H: res.H,
      halfW: res.halfW,
      halfH: res.halfH,
      ax: res.ax,
      ay: res.ay,
      reqScale,
      // Lane VW: 'preview' = the capped instant tier (full-density solve
      // still cooking in the worker), 'full' = the swapped tessellation-law
      // mesh, 'sync' = the on-thread fallback matrix rows, null = no volume.
      volumeQuality: res.volumeQuality || null,
    };
  }
  stats.asyncRuns += 1;
  stats.lastAsyncMs = performance.now() - t0;
  obj.dirty = true;
  // Child dirty does NOT bubble in v6 — invalidate ancestor group caches too.
  let g = obj.group;
  while (g) { g.set('dirty', true); g = g.group; }
  obj.canvas?.requestRenderAll();
  poolCheckSoon(obj.canvas); // a stash landed — re-account the global pool
}

// ---------------------------------------------------------------- viewport patches
// (2026-07-18) FULL DEVICE RESOLUTION AT ANY ZOOM on print-size documents.
// When a whole-object stash cannot deliver the current draw scale (the live
// side/area budgets bind — a 42in headline at 200% zoom needs ~29Mpx and
// >10k px on a side), the capped whole-object stash stays as the BASE and a
// PATCH is laid over it: the viewport∩object slice, expanded ~15%, rendered
// at FULL device resolution through the SAME pipeline (source steps bake an
// asymmetric-frustum region of the same frame; filters run on the slice with
// a spill guard). Registration is arithmetic, not sampling: every canvas
// carries its exact object-unit rect and both bitmaps draw through the same
// ctx transform.
//
// Lifecycle: debounced ~250ms behind viewport changes (useCanvasEditor hooks
// after:render → fxNotifyViewportChange); one bake in flight per object,
// newest-wins; invalidated by the scale-free content hash + coverage +
// draw-scale drift; dropped when the base alone can serve the zoom, when the
// object leaves the viewport, or when editing starts (WebGL fence: never a
// bake while isEditing). While a patch stands the object's fabric cache is
// OFF (the cache's own side cap would resample the patch away — the exact
// bind measured in the repro); the previous objectCaching value is restored
// on drop.
const PATCH_DEBOUNCE_MS = 250;
const PATCH_STEP_PX = 64; // viewport-rect quantum (device px) — anti-thrash
const PATCH_MARGIN = 0.15; // per-side pan headroom, fraction of the viewport span

const patchTimers = new WeakMap(); // canvas → debounce timer id

/** Debounced entry — call on ANY viewport/layout change; cheap to spam. */
export function fxNotifyViewportChange(canvas) {
  if (FX_DISABLED || !canvas || exportDepth > 0) return;
  stats.patchNotifies += 1;
  const prev = patchTimers.get(canvas);
  if (prev) clearTimeout(prev);
  patchTimers.set(canvas, setTimeout(() => {
    patchTimers.delete(canvas);
    try { evaluateCanvasPatches(canvas); } catch (e) { logOnce('patch eval failed', e); }
  }, PATCH_DEBOUNCE_MS));
}

function evaluateCanvasPatches(canvas) {
  if (FX_DISABLED || exportDepth > 0 || !canvas.getObjects) return;
  stats.patchEvals += 1;
  // Pool recency stamps: one tick per evaluator pass; every fx object found
  // on-viewport carries the tick. The stamps ORDER eviction candidates
  // (least-recently-seen first) — live visibility is re-proven at evict time.
  poolTickCounter += 1;
  forEachFxObject(canvas, (o) => {
    try { if (objOnViewport(o)) o.__fxSeenTick = poolTickCounter; } catch (_) { /* stamp only */ }
  });
  for (const obj of canvas.getObjects()) {
    if (!obj.__fxAttached) {
      // Grouped fx objects render through the PARENT's cache — caching-off
      // on the child cannot bypass it, so patches skip them (documented
      // residual limit); an object grouped WITH a standing patch sheds it.
      if (typeof obj.getObjects === 'function') {
        for (const child of obj.getObjects()) {
          if (child.__fxAttached && (child.__fxPatch || child.__fxPatchWant)) dropPatch(child);
        }
      }
      continue;
    }
    const steps = obj.data && obj.data.fx;
    if (!Array.isArray(steps) || steps.length === 0) {
      dropPatch(obj);
      continue;
    }
    try { evaluateObjectPatch(obj, steps); } catch (e) { logOnce('patch eval(obj) failed', e); }
  }
  // Governance rides the same debounced pass (viewport changes are exactly
  // when the on/off-viewport working set moves).
  try { poolCheck(canvas); } catch (e) { logOnce('pool check failed', e); }
}

/** The object-space rect the current raster spans: the stash's real frame
 * when one stands (bake framing — tighter than geo for sourced stacks), else
 * the filter geometry. */
function rasterRectOf(obj, geo) {
  const st = obj.__fxStash;
  if (st && st.canvas) {
    return {
      x0: -st.halfW - (st.ax || 0), y0: -st.halfH - (st.ay || 0),
      x1: st.halfW - (st.ax || 0), y1: st.halfH - (st.ay || 0),
    };
  }
  return { x0: -geo.halfW, y0: -geo.halfH, x1: geo.halfW, y1: geo.halfH };
}

/** Viewport AABB in the object's LOCAL space (center-origin object units —
 * the exact space _render/drawResultOnCtx paint in). Null on any hiccup. */
function viewportRectInLocal(obj) {
  const c = obj.canvas;
  if (!c || !c.viewportTransform) return null;
  const inv = util.invertTransform(c.viewportTransform);
  const minv = util.invertTransform(obj.calcTransformMatrix());
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const [cx, cy] of [[0, 0], [c.width, 0], [0, c.height], [c.width, c.height]]) {
    const scene = util.transformPoint({ x: cx, y: cy }, inv);
    const loc = util.transformPoint(scene, minv);
    if (loc.x < x0) x0 = loc.x;
    if (loc.x > x1) x1 = loc.x;
    if (loc.y < y0) y0 = loc.y;
    if (loc.y > y1) y1 = loc.y;
  }
  if (!Number.isFinite(x0) || !Number.isFinite(y1)) return null;
  return { x0, y0, x1, y1 };
}

const intersectRect = (a, b) => {
  const r = {
    x0: Math.max(a.x0, b.x0), y0: Math.max(a.y0, b.y0),
    x1: Math.min(a.x1, b.x1), y1: Math.min(a.y1, b.y1),
  };
  return r.x1 - r.x0 > 1e-6 && r.y1 - r.y0 > 1e-6 ? r : null;
};

const coversRect = (outer, inner, eps) => outer.x0 <= inner.x0 + eps && outer.y0 <= inner.y0 + eps
  && outer.x1 >= inner.x1 - eps && outer.y1 >= inner.y1 - eps;

/** True draw scale — object × group × zoom × retina, UNCRUSHED by any cache
 * cap (the seam's __fxLastScale reads the cache ctx, which lies under a
 * capped cache; the patch decision must not). */
function trueScaleOf(obj) {
  const s = obj.getTotalObjectScaling();
  return Math.max(0.02, (Math.abs(s.x) + Math.abs(s.y)) / 2);
}

/** Max px/unit the BASE machinery can deliver for this raster span under the
 * live budgets (side on either axis + the area budget). Uses the exact
 * ceilings the stash bake would (renderBoundsNow — eval only ever runs at
 * exportDepth 0, so these are the live numbers). */
function baseCapPPU(rect) {
  const { maxSide, maxArea } = renderBoundsNow();
  const w = Math.max(1e-6, rect.x1 - rect.x0);
  const h = Math.max(1e-6, rect.y1 - rect.y0);
  return Math.min(maxSide / w, maxSide / h, Math.sqrt(maxArea / (w * h)));
}

/** Max px/unit the base machinery ACTUALLY delivers for this object — the
 * live budget ceiling over the raster span AND fabric's own cache limiter.
 *
 * "Base machinery" INCLUDES fabric's object cache (2026-07-20, V1 board):
 * while caching is on, _createCacheCanvas runs _limitCacheSize (side ≤
 * maxCacheSideLimit, area ≤ perfLimitSizeTotal) and the seam bakes at that
 * CRUSHED ctx scale — the glass can never show more than the limited cache
 * delivers, however high the stash bakes. The budget cap alone (baseCapPPU)
 * overestimates in the band where the cache limiter binds first; GlyphTrace
 * v5's true-font tracing narrowed raster spans, moving real mid zooms (200%
 * on the 42×63 repro: cache 2.07 < need 3.00 < budget cap 4.6) into that
 * dead band — delivered 0.75-0.83 with no patch. Measure the cache ceiling
 * with fabric's OWN arithmetic (pure functions of transform + config,
 * stable whether caching is currently toggled off by a standing patch — no
 * feedback flap). Used by BOTH the evaluator threshold and bakePatchNow's
 * land-time re-check (a land-check on the budget cap alone would discard
 * every patch the evaluator just asked for in the dead band). Fallback =
 * the old budget-only behavior. */
function servedBasePPU(obj, raster) {
  let served = baseCapPPU(raster);
  try {
    if (typeof obj._getCacheCanvasDimensions === 'function'
      && typeof obj._limitCacheSize === 'function') {
      const dims = obj._limitCacheSize(obj._getCacheCanvasDimensions());
      const zx = Math.abs(dims.zoomX || 0);
      const zy = Math.abs(dims.zoomY || 0);
      if (zx > 0 && zy > 0) served = Math.min(served, (zx + zy) / 2);
    }
  } catch (e) { logOnce('patch eval cache-cap probe failed', e); }
  return served;
}

function evaluateObjectPatch(obj, steps) {
  if (obj.isEditing || obj.__fxDragTilt) { dropPatch(obj); return; }
  const scale = trueScaleOf(obj);
  const geo = fxGeometry(obj, steps);
  const raster = rasterRectOf(obj, geo);
  // Threshold (with 2% hysteresis): patch only when the base machinery
  // CANNOT deliver this scale (budget caps AND the fabric cache limiter —
  // servedBasePPU). Symmetric — the same test drops the patch on the way
  // back down.
  if (scale <= servedBasePPU(obj, raster) * 1.02) { dropPatch(obj); return; }
  const vp = viewportRectInLocal(obj);
  if (!vp) { dropPatch(obj); return; }
  const visible = intersectRect(vp, raster);
  if (!visible) { dropPatch(obj); return; } // object off-viewport
  // Pan headroom: expand each side by PATCH_MARGIN of the viewport span,
  // then quantize OUTWARD to the 64-device-px grid (pans inside the margin
  // and sub-quantum jitters re-use the standing patch — anti-thrash).
  const mx = (vp.x1 - vp.x0) * PATCH_MARGIN;
  const my = (vp.y1 - vp.y0) * PATCH_MARGIN;
  const grid = PATCH_STEP_PX / scale;
  let want = {
    x0: Math.floor((visible.x0 - mx) / grid) * grid,
    y0: Math.floor((visible.y0 - my) / grid) * grid,
    x1: Math.ceil((visible.x1 + mx) / grid) * grid,
    y1: Math.ceil((visible.y1 + my) / grid) * grid,
  };
  want = intersectRect(want, raster);
  if (!want) { dropPatch(obj); return; }
  // Patch resolution: full device scale, capped by the live budget over the
  // patch's own span (at sane zooms the visible slice is far under it).
  const { maxSide, maxArea } = renderBoundsNow();
  const pw = (want.x1 - want.x0) * scale;
  const ph = (want.y1 - want.y0) * scale;
  let k = Math.min(1, maxSide / Math.max(pw, ph));
  if (pw * k * (ph * k) > maxArea) k *= Math.sqrt(maxArea / (pw * k * ph * k));
  const patchScale = Math.max(0.02, scale * k);

  const p = obj.__fxPatch;
  const bh = fxBaseHashOf(obj, steps);
  if (p && p.canvas && p.baseHash === bh
    && Math.abs(patchScale / p.scale - 1) <= 0.02
    && coversRect(p.rect, visible, grid * 0.5)) {
    return; // the standing patch still serves this viewport
  }
  schedulePatchBake(obj, { rect: want, scale: patchScale, baseHash: bh });
}

/** Newest-wins, one in flight: a want landing mid-bake replaces the queued
 * target; the loop drains until the latest want is the one on the object. */
function schedulePatchBake(obj, want) {
  obj.__fxPatchWant = want;
  if (obj.__fxPatchBusy) return;
  obj.__fxPatchBusy = true;
  (async () => {
    try {
      while (obj.__fxPatchWant && obj.__fxAttached && !FX_DISABLED) {
        const w = obj.__fxPatchWant;
        obj.__fxPatchWant = null;
        await bakePatchNow(obj, w).catch((e) => logOnce('patch bake failed', e));
      }
    } finally {
      obj.__fxPatchBusy = false;
    }
  })();
}

/** Filter-guard margin (object units): spatial filters (bevel EDT, contact
 * shadow) read a neighborhood — a silhouette cut at the patch edge would
 * shade wrongly within their reach, so the bake rect grows by it and the
 * final crop removes it. Source steps guard their own reach (rim) inside
 * applyAsync. */
function patchGuardU(obj, steps, geo, scale) {
  let g = 0;
  const consumed = consumedIdsOf(steps);
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (!def || def.kind === 'source') continue;
    if (consumed && consumed.has(step.id)) continue;
    try {
      const params = def.sanitize(step.params || {});
      if (def.isEmpty(params)) continue;
      g += Math.max(0, def.spill(params, geo.u) || 0);
    } catch (_) { /* margin only */ }
  }
  return g > 0 ? g * 1.25 + 4 / scale : 0;
}

async function bakePatchNow(obj, want) {
  if (FX_DISABLED || exportDepth > 0 || !obj.__fxAttached || !obj.canvas) return;
  const steps = obj.data && obj.data.fx;
  if (!Array.isArray(steps) || steps.length === 0) return;
  // WebGL fence + joystick ownership: never mid-edit, never mid-drag.
  if (obj.isEditing || obj.__fxDragTilt) return;
  if (fxBaseHashOf(obj, steps) !== want.baseHash) return; // content moved on
  const t0 = performance.now();
  const geo = fxGeometry(obj, steps);
  const guardU = patchGuardU(obj, steps, geo, want.scale);
  const clampRect = hasActiveSourceStep(steps)
    ? rasterRectOf(obj, geo)
    : { x0: -geo.halfW, y0: -geo.halfH, x1: geo.halfW, y1: geo.halfH };
  const rectBake = intersectRect({
    x0: want.rect.x0 - guardU, y0: want.rect.y0 - guardU,
    x1: want.rect.x1 + guardU, y1: want.rect.y1 + guardU,
  }, clampRect) || want.rect;
  const lease = makeLease(false); // patch canvases live on the object, never pooled
  const res = await runChainPatch(obj, steps, geo, want.scale, rectBake, lease);
  if (!res || !res.canvas) return;
  if (!obj.__fxAttached || !obj.canvas) return;
  if (fxBaseHashOf(obj, obj.data?.fx || []) !== want.baseHash) return; // superseded
  // Superseded by a zoom-out mid-bake: landing would resurrect a patch the
  // evaluator just dropped (and flip caching back off at fit). Re-check the
  // threshold with CURRENT numbers before landing — the SAME served-PPU test
  // the evaluator uses (budget caps AND the fabric cache limiter), or every
  // dead-band patch would be asked for and then discarded right here.
  if (obj.isEditing || trueScaleOf(obj) <= servedBasePPU(obj, rasterRectOf(obj, geo)) * 1.02) return;
  // ONE final crop: delivered rect → the wanted draw rect (removes filter
  // guards and any source-side padding in a single resample-free blit).
  const ppuX = res.canvas.width / Math.max(1e-6, res.rect.x1 - res.rect.x0);
  const ppuY = res.canvas.height / Math.max(1e-6, res.rect.y1 - res.rect.y0);
  const cx0 = Math.min(res.canvas.width - 1, Math.max(0, Math.floor((want.rect.x0 - res.rect.x0) * ppuX)));
  const cy0 = Math.min(res.canvas.height - 1, Math.max(0, Math.floor((want.rect.y0 - res.rect.y0) * ppuY)));
  const cx1 = Math.max(cx0 + 1, Math.min(res.canvas.width, Math.ceil((want.rect.x1 - res.rect.x0) * ppuX)));
  const cy1 = Math.max(cy0 + 1, Math.min(res.canvas.height, Math.ceil((want.rect.y1 - res.rect.y0) * ppuY)));
  let outCanvas = res.canvas;
  let outRect = res.rect;
  if (cx0 > 0 || cy0 > 0 || cx1 < res.canvas.width || cy1 < res.canvas.height) {
    const cropped = document.createElement('canvas');
    cropped.width = cx1 - cx0;
    cropped.height = cy1 - cy0;
    cropped.getContext('2d').drawImage(res.canvas, cx0, cy0, cx1 - cx0, cy1 - cy0, 0, 0, cx1 - cx0, cy1 - cy0);
    outCanvas = cropped;
    outRect = {
      x0: res.rect.x0 + cx0 / ppuX, y0: res.rect.y0 + cy0 / ppuY,
      x1: res.rect.x0 + cx1 / ppuX, y1: res.rect.y0 + cy1 / ppuY,
    };
  }
  obj.__fxPatch = {
    baseHash: want.baseHash,
    rect: outRect,
    canvas: outCanvas,
    scale: want.scale,
    ppu: (ppuX + ppuY) / 2,
  };
  // The fabric cache would resample the patch back down (its own side cap is
  // exactly what binds at these zooms) — render direct while patched.
  if (obj.__fxPatchPrevCaching === undefined) {
    obj.__fxPatchPrevCaching = obj.objectCaching;
    obj.objectCaching = false;
  }
  stats.patchBakes += 1;
  stats.lastPatchMs = performance.now() - t0;
  obj.dirty = true;
  let g = obj.group;
  while (g) { g.set('dirty', true); g = g.group; }
  obj.canvas?.requestRenderAll();
  poolCheckSoon(obj.canvas); // a patch landed — re-account the global pool
}

/** Region variant of runChainFull: base render + die-cut closing + source
 * bakes + filters all over ONE object-unit sub-rect at `scale` px/unit.
 * Steps see the region through env.patchRegion; a source step returns
 * {canvas, patchRect} (its exact delivered rect) instead of the whole-object
 * mapping. A throwing source step aborts the patch (the base stash stands —
 * a flat patch over a 3D base would be worse than soft). */
async function runChainPatch(obj, steps, geo, scale, rect, lease) {
  const base = obj.__fxBase;
  if (!base) return null;
  const rendered = renderBaseOffscreen(obj, base, geo, scale, 'full', lease, rect);
  const { env } = rendered;
  env.reqScale = scale;
  env.patchRegion = rect;
  let cur = rendered.canvas;
  let outRect = { ...rect };
  const consumed = consumedIdsOf(steps);
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (!def) continue;
    if (def.kind !== 'source' && consumed && consumed.has(step.id)) continue;
    const params = def.sanitize(step.params || {});
    if (def.isEmpty(params)) continue;
    env.params = params;
    if (def.kind === 'source') {
      if (typeof def.applyAsync !== 'function') continue;
      stats.sourceBakes += 1;
      const tS = performance.now();
      try {
        const out = await def.applyAsync(cur, env);
        if (!out || !out.canvas || !out.patchRect) return null;
        cur = out.canvas;
        outRect = out.patchRect;
        env.width = cur.width;
        env.height = cur.height;
        env.scale = cur.width / Math.max(1e-6, outRect.x1 - outRect.x0);
      } catch (e) {
        stats.sourceErrors += 1;
        logOnce(`source(${step.id}) patch bake failed`, e);
        return null;
      } finally {
        stats.lastSourceMs = performance.now() - tS;
      }
    } else if (typeof def.applyAsync === 'function') {
      cur = (await def.applyAsync(cur, env)) || cur;
    } else if (typeof def.applySync === 'function') {
      cur = def.applySync(cur, env) || cur;
    }
  }
  return { canvas: cur, rect: outRect };
}

/** Drop the patch and restore the object's caching. Safe to call always. */
function dropPatch(obj) {
  if (obj.__fxPatchWant) obj.__fxPatchWant = null;
  const had = !!obj.__fxPatch;
  if (had) {
    delete obj.__fxPatch;
    stats.patchDrops += 1;
  }
  if (obj.__fxPatchPrevCaching !== undefined) {
    obj.objectCaching = obj.__fxPatchPrevCaching;
    delete obj.__fxPatchPrevCaching;
  }
  if (had) {
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
  }
}

/** Draw the standing patch over the base stash (stash-hit / stale-stash
 * branches). Skipped while the joystick tilts the raster (the patch must not
 * fight the trackball transform) and whenever the content hash moved on.
 *
 * DEVICE-GRID SNAP (2026-07-18 wysiwyg-governance lane): at uncapped zooms
 * the patch's ppu EQUALS the device ppu — but the object's screen position is
 * an arbitrary sub-pixel (any pan; DPR 1.5 turns integer css translations
 * into half-DEVICE-pixel offsets), so a naive drawImage bilinear-blends
 * adjacent patch pixels 50/50. probe-wysiwyg measured that phase against the
 * REAL print path: mean |Δ| 2.7/255, p99 24/255 on EDT bevel band edges
 * (smooth 3D content hides it). Snapping the DESTINATION rect by the
 * sub-pixel phase makes the blit exactly 1:1 — the art lands ≤0.5 device px
 * (≤0.2 css px at 250%) from its true position, far under the 0.75px edge
 * contract, and pixel-crisp. Applied only when the mapping is axis-aligned
 * and the ppu matches the ctx scale to 1e-4 (an uncapped patch at the eval
 * zoom); capped patches, rotated objects and drifted zooms resample anyway
 * and draw exactly as before. */
/** The patch that WILL draw for this render — null unless standing, valid
 * for the current content hash, and not fenced off by a joystick tilt. The
 * base-exclusion clip (below) and the actual draw must agree on this. */
function drawablePatchOf(obj, steps) {
  const p = obj.__fxPatch;
  if (!p || !p.canvas || obj.__fxDragTilt) return null;
  return p.baseHash === fxBaseHashOf(obj, steps) ? p : null;
}

/** The destination rect the patch will occupy on this ctx — the device-grid
 * snap lives here so the draw and the base-exclusion clip use ONE geometry. */
function patchDestRectOf(p, ctx) {
  const spanX = p.rect.x1 - p.rect.x0;
  const spanY = p.rect.y1 - p.rect.y0;
  try {
    const t = typeof ctx.getTransform === 'function' ? ctx.getTransform() : null;
    if (t && Math.abs(t.b) < 1e-6 && Math.abs(t.c) < 1e-6 && t.a > 0 && t.d > 0) {
      const ppuX = p.canvas.width / Math.max(1e-9, spanX);
      const ppuY = p.canvas.height / Math.max(1e-9, spanY);
      if (Math.abs(t.a / ppuX - 1) < 1e-4 && Math.abs(t.d / ppuY - 1) < 1e-4) {
        const dvx = t.a * p.rect.x0 + t.e;
        const dvy = t.d * p.rect.y0 + t.f;
        return {
          x0: p.rect.x0 + (Math.round(dvx) - dvx) / t.a,
          y0: p.rect.y0 + (Math.round(dvy) - dvy) / t.d,
          // Exact 1:1 span: widthPx / devicePPU — with the ppu gate above
          // this differs from the geometric span by <1e-4 (sub-0.3px at the
          // far edge) and guarantees integer→integer sampling.
          w: p.canvas.width / t.a,
          h: p.canvas.height / t.d,
        };
      }
    }
  } catch (_) { /* geometric fallback below */ }
  return { x0: p.rect.x0, y0: p.rect.y0, w: spanX, h: spanY };
}

/** Draw `draw()` with the ctx clipped to everything EXCEPT rect (even-odd).
 * The patch is the COMPLETE object composite for its rect — the capped-soft
 * base must not paint under it: at the patch's AA edge pixels the soft
 * base's wider edges bleed through the partial alpha (probe-wysiwyg measured
 * the bevel family at mean 2.98 → 0.41, p99 24 → 5.3 per 255 against the
 * print path when the base is excluded). The print path composites the bake
 * over the clean underlying canvas; excluding the base makes the screen do
 * exactly the same. */
function withRectExcluded(ctx, rect, draw) {
  ctx.save();
  try {
    const path = new Path2D();
    const big = 1e7;
    path.rect(-big, -big, big * 2, big * 2);
    path.rect(rect.x0, rect.y0, rect.w, rect.h);
    ctx.clip(path, 'evenodd');
    draw();
  } finally {
    ctx.restore();
  }
}

function drawPatchOnCtx(obj, ctx, steps) {
  const p = drawablePatchOf(obj, steps);
  if (!p) return;
  const d = patchDestRectOf(p, ctx);
  ctx.drawImage(p.canvas, 0, 0, p.canvas.width, p.canvas.height, d.x0, d.y0, d.w, d.h);
}

// ---------------------------------------------------------------- stash pool
// GLOBAL memory governance for live fx bitmaps (2026-07-18 wysiwyg-governance
// lane — the school-PC contract: "easily run on any system 8gb+"). Every
// standing fx bitmap on the document — whole-object stashes (__fxStash) and
// viewport patches (__fxPatch), including the joystick's low-res drag stashes
// — is accounted against ONE global byte budget (renderCaps.fxPoolBudget:
// 256MB ≤8GB / 384MB >8GB / 512MB ≥16GB). Over budget → evict the bitmaps of
// the objects LEAST RECENTLY ON-VIEWPORT down to ~80% of budget (hysteresis:
// enter at 100%, evict to 80% — a landing bake can't ping-pong the pool).
//
// Eviction drops to the EXISTING flat-fallback lifecycle: the seam already
// renders flat on a missing stash and schedules the re-bake from the next
// _render — which for an offscreen object happens only when it re-enters the
// viewport (fabric's skipOffscreen culling never renders it before that).
// The object's fabric cache is freed with the stash (it holds a full COPY of
// the stash pixels) and the object marked dirty, so the freed bytes are real.
//
// NEVER evicted: the active/selected object (and ActiveSelection members),
// anything mid-bake (__fxTimer / __fxPatchBusy / __fxPatchWant /
// __fxBakeBusy), patches of on-viewport objects, and anything while an
// export bracket is open (export correctness outranks the live budget;
// __fxExportStash is never counted — fxCleanupExport frees it inside the
// bracket). Tier 2, last resort: an ON-viewport object whose full-res patch
// stands may lose its BASE stash — the patch covers every visible pixel at
// >= the base's resolution (the base only backs the off-viewport remainder),
// and the seam's patch-only branch (renderFxStack) keeps drawing it without
// re-scheduling the base, so tier-2 cannot thrash. AVAILABILITY degrades
// (a re-bake happens later, off the interaction path); CORRECTNESS never
// does (no wrong/soft pixels while visible).
const POOL_HYSTERESIS = 0.8; // evict down to this fraction of the budget
const POOL_CHECK_DEBOUNCE_MS = 120;

const pool = {
  bytes: 0, // last reconciled total (stashes + patches)
  budget: 0, // ladder value at the last check
  evictions: 0, // objects whose live fx bitmaps governance freed
  tier2Evictions: 0, // on-viewport BASE evictions under a covering patch
  evictedBytes: 0, // cumulative bytes freed
  overBudget: false, // true when the never-evict laws blocked reaching budget
  lastCheckMs: 0,
};
let poolTickCounter = 0;
const poolTimers = new WeakMap(); // canvas → debounce timer id

const bytesOfCanvas = (cv) => (cv ? cv.width * cv.height * 4 : 0);

function freeCanvasStore(cv) {
  // Collapse the backing store NOW — GC would get there eventually, but the
  // pool's whole point is bounding the instantaneous footprint.
  try { cv.width = 1; cv.height = 1; } catch (_) { /* detached */ }
}

/** Walk every fx-attached object, nested groups included. */
function forEachFxObject(canvas, fn) {
  const walk = (objs) => {
    for (const o of objs || []) {
      if (!o) continue;
      if (o.__fxAttached) fn(o);
      if (typeof o.getObjects === 'function') walk(o.getObjects());
    }
  };
  walk(canvas.getObjects ? canvas.getObjects() : []);
}

/** Live on-viewport truth at eviction time (stamps only order candidates —
 * this decides). Fail-open: an object we cannot measure is treated as
 * visible and never evicted on doubt. */
function objOnViewport(obj) {
  try {
    const steps = Array.isArray(obj.data && obj.data.fx) ? obj.data.fx : [];
    const vp = viewportRectInLocal(obj);
    if (!vp) return true;
    return !!intersectRect(vp, rasterRectOf(obj, fxGeometry(obj, steps)));
  } catch (_) {
    return true;
  }
}

function poolProtectedSet(canvas) {
  const set = new Set();
  try {
    const act = typeof canvas.getActiveObject === 'function' && canvas.getActiveObject();
    if (act) {
      set.add(act);
      if (typeof act.getObjects === 'function') for (const o of act.getObjects()) set.add(o);
    }
  } catch (_) { /* selection unreadable — nothing protected beyond mid-bake */ }
  return set;
}

const poolMidBake = (o) => !!(o.__fxTimer || o.__fxPatchBusy || o.__fxPatchWant || o.__fxBakeBusy);

/** Free an object's governed bitmaps. which: 'all' (offscreen tier) frees
 * patch + stash + fabric cache; 'base' (tier 2) frees the stash + cache but
 * KEEPS the standing patch (and the caching-off that lets it draw). */
function poolEvict(obj, which) {
  let freed = 0;
  if (which === 'all' && obj.__fxPatch) {
    freed += bytesOfCanvas(obj.__fxPatch.canvas);
    freeCanvasStore(obj.__fxPatch.canvas);
    dropPatch(obj); // restores objectCaching; counts a patchDrop
  }
  const st = obj.__fxStash;
  if (st && st.canvas) {
    freed += bytesOfCanvas(st.canvas);
    freeCanvasStore(st.canvas);
    delete obj.__fxStash;
  }
  // The fabric cache duplicates the stash pixels at draw scale — free it and
  // dirty the chain so the next VISIBLE render rebuilds it through the seam.
  if (obj._cacheCanvas) freeCanvasStore(obj._cacheCanvas);
  obj.dirty = true;
  let g = obj.group;
  while (g) { g.set('dirty', true); g = g.group; }
  pool.evictions += 1;
  stats.poolEvictions += 1;
  if (which === 'base') pool.tier2Evictions += 1;
  pool.evictedBytes += freed;
  return freed;
}

/** The governance pass: account the graph, evict past-budget LRU holders. */
function poolCheck(canvas) {
  if (FX_DISABLED || exportDepth > 0 || !canvas || !canvas.getObjects) return;
  const t0 = performance.now();
  stats.poolChecks += 1;
  pool.budget = getRenderCaps().fxPoolBudget || 256 * 1024 * 1024;
  let total = 0;
  const holders = [];
  forEachFxObject(canvas, (o) => {
    const b = bytesOfCanvas(o.__fxStash && o.__fxStash.canvas)
      + bytesOfCanvas(o.__fxPatch && o.__fxPatch.canvas);
    if (b > 0) {
      holders.push(o);
      total += b;
    }
  });
  pool.bytes = total;
  if (total <= pool.budget) {
    pool.overBudget = false;
    pool.lastCheckMs = performance.now() - t0;
    return;
  }
  const floor = pool.budget * POOL_HYSTERESIS;
  const protectedSet = poolProtectedSet(canvas);
  holders.sort((a, b) => (a.__fxSeenTick || 0) - (b.__fxSeenTick || 0));
  // Tier 1: objects NOT currently on-viewport — free everything they hold.
  // Grouped children are never evicted (they render through the parent's
  // cache — a dirty child would flat-render INTO an on-screen group cache
  // and re-bake immediately; same residual-limit posture as patches).
  for (const o of holders) {
    if (total <= floor) break;
    if (protectedSet.has(o) || poolMidBake(o) || o.group) continue;
    if (objOnViewport(o)) continue;
    total -= poolEvict(o, 'all');
  }
  // Tier 2 (last resort): on-viewport BASES fully backed by a standing patch.
  if (total > floor) {
    for (const o of holders) {
      if (total <= floor) break;
      if (protectedSet.has(o) || poolMidBake(o) || o.group) continue;
      if (!o.__fxStash || !o.__fxStash.canvas) continue;
      const p = o.__fxPatch;
      if (!p || !p.canvas) continue;
      total -= poolEvict(o, 'base');
    }
  }
  pool.bytes = total;
  pool.overBudget = total > pool.budget;
  if (pool.overBudget) {
    warnOnce(`pool-over:${Math.round(pool.budget / (1 << 20))}`,
      `live-fx pool over budget after eviction: ${Math.round(total / (1 << 20))}MB held vs `
      + `${Math.round(pool.budget / (1 << 20))}MB budget — the selected/mid-bake/visible working set `
      + `alone exceeds the device tier. Correctness kept; budget exceeded (documented posture).`);
  }
  pool.lastCheckMs = performance.now() - t0;
}

/** Debounced pool check — call after any stash/patch lands. Cheap to spam. */
function poolCheckSoon(canvas) {
  if (FX_DISABLED || exportDepth > 0 || !canvas) return;
  const prev = poolTimers.get(canvas);
  if (prev) clearTimeout(prev);
  poolTimers.set(canvas, setTimeout(() => {
    poolTimers.delete(canvas);
    try { poolCheck(canvas); } catch (e) { logOnce('pool check failed', e); }
  }, POOL_CHECK_DEBOUNCE_MS));
}

function renderFxStack(obj, ctx, base, steps) {
  const t = typeof ctx.getTransform === 'function' ? ctx.getTransform() : null;
  const scaleX = t ? Math.hypot(t.a, t.b) : obj.getTotalObjectScaling().x;
  const scaleY = t ? Math.hypot(t.c, t.d) : obj.getTotalObjectScaling().y;
  const scale = Math.max(0.02, (scaleX + scaleY) / 2);
  obj.__fxLastScale = scale;

  // A patch must never stand while the user edits: the WebGL fence forbids a
  // refresh, and a beveled patch would fight the per-keystroke sync render.
  // Cheap drop here (no bake, no timers) — re-armed after exit by the
  // under-delivery trigger below.
  if (obj.isEditing && obj.__fxPatch) dropPatch(obj);

  const sourced = hasSourceStep(steps);

  // Mid-edit with a source step: plain base, full stop. The 3D raster is laid
  // out by the bake, not by fabric's text metrics — the caret would float over
  // pixels that don't match it — and WebGL must never run per keystroke. The
  // stash is deliberately NOT drawn even when its hash still matches.
  if (sourced && obj.isEditing && exportDepth === 0) {
    base.call(obj, ctx);
    return;
  }

  const geo = fxGeometry(obj, steps);
  const hash = fxHashOf(obj, steps, scale);
  // The raster about to be drawn is a 3D bake (not the 2D base): the bake
  // does not paint textBackgroundColor, so the seam draws the highlight rows
  // itself as a live 2D underlay behind the standing letters. Active-source
  // only — an empty source step leaves the 2D base (which paints its own
  // highlight) as the raster, and an underlay would double-draw it.
  const bakedRaster = hasActiveSourceStep(steps);

  // Export render with a source step — or a PUFFY sticker stack (a filter
  // whose full-quality result needs the WebGL slab bake; sticker v3 lane):
  // WebGL cannot run synchronously here. Draw the primed export stash
  // (fxPrimeExport ran before the render pass), else the screen stash at
  // whatever resolution it holds, else fall through to base + filter steps
  // (for the puffy stack that sync chain paints the honest flat-dome
  // degrade; source steps simply have no sync path by contract).
  const glFiltered = exportDepth > 0 && !sourced && stickerWantsGlBake(steps);
  if (exportDepth > 0 && (sourced || glFiltered)) {
    const bh = fxBaseHashOf(obj, steps);
    const ex = obj.__fxExportStash;
    if (ex && ex.hash === bh && ex.canvas) {
      if (bakedRaster) drawSourcedUnderlays(obj, ctx);
      drawResultOnCtx(ctx, ex.canvas, ex.W, ex.H, ex.halfW, ex.halfH, ex.ax, ex.ay);
      return;
    }
    const sc = obj.__fxStash;
    if (sc && sc.baseHash === bh && sc.canvas) {
      if (bakedRaster) drawSourcedUnderlays(obj, ctx);
      // No-silent-caps: the SCREEN stash is standing in for a print bake —
      // its resolution is whatever the screen needed, almost never the
      // multiplier. Loud note with the delivered px so a soft export is
      // never a mystery.
      warnOnce(`export-screen-stash:${sc.W}x${sc.H}`,
        `export drew a SCREEN-resolution stash (${sc.W}x${sc.H}) for a ${sourced ? 'source step' : 'puffy sticker'} — `
        + `fxPrimeExport did not run or was superseded; expect softness at print size.`);
      drawResultOnCtx(ctx, sc.canvas, sc.W, sc.H, sc.halfW, sc.halfH, sc.ax, sc.ay);
      return;
    }
    if (sourced) {
      logOnce('export: no primed bake for a source step — exported flat');
    } else {
      logOnce('export: no primed bake for a puffy sticker — flat-dome plate exported');
    }
  }

  // Full-quality stash hit: zero filter work, one drawImage. A same-hash
  // stash BAKED FOR less than this draw (a joystick drag's {scaleMul:0.6}
  // bake whose full-res release bake lost the race, or a stash cut before a
  // cap raise) still draws — no flash — but self-expires: the debounced
  // upgrade replaces it at full resolution (convergence after ANY
  // interaction, the post-drag guarantee).
  const st = obj.__fxStash;
  if (exportDepth === 0 && st && st.hash === hash && st.canvas) {
    if (bakedRaster) drawSourcedUnderlays(obj, ctx);
    const pd = drawablePatchOf(obj, steps);
    if (pd) {
      // Base everywhere EXCEPT the patch's footprint, then the patch — the
      // patch owns its pixels completely (see withRectExcluded).
      const dest = patchDestRectOf(pd, ctx);
      withRectExcluded(ctx, dest, () => drawStashOnCtx(obj, ctx, st));
      ctx.drawImage(pd.canvas, 0, 0, pd.canvas.width, pd.canvas.height,
        dest.x0, dest.y0, dest.w, dest.h);
    } else {
      drawStashOnCtx(obj, ctx, st);
      if (!obj.__fxPatch && !obj.isEditing && !obj.__fxDragTilt) {
        // Under-delivery safety net: the stash cannot serve this draw — patch
        // territory. Debounced through the same evaluator the viewport hookup
        // uses (covers zoomed-in loads and editing exits that no viewport
        // event follows). The ctx scale is a LIE under a capped fabric cache
        // (it reads the crushed cache zoom — measured 1.85 for a true 3.0),
        // so cache renders also test the true uncrushed scale; that runs only
        // on cache REDRAWS (rare), never per frame.
        const deliveredPPU = st.W / (st.halfW * 2);
        if (scale > deliveredPPU * 1.05
          || (obj.ownCaching === true && trueScaleOf(obj) > deliveredPPU * 1.05)) {
          fxNotifyViewportChange(obj.canvas);
        }
      }
    }
    if (stashUnderRes(obj, st, scale)) scheduleAsync(obj);
    return;
  }

  // Source step with a STALE stash (param scrub — joystick/slider drag —
  // or a zoom-bucket hop): keep the last bake on screen instead of flashing
  // flat; the debounce below chases the new hash and replaces it. Never
  // while editing (the mid-edit branch above already returned plain base —
  // the caret must match fabric's metrics) and never for exports (they draw
  // primed bakes only, handled above). The patch (validated against the
  // scale-free hash) still draws over a zoom-bucket-stale base — the content
  // is identical, only the base's resolution lags.
  if (exportDepth === 0 && sourced && st && st.canvas) {
    if (bakedRaster) drawSourcedUnderlays(obj, ctx);
    const pd = drawablePatchOf(obj, steps);
    if (pd) {
      const dest = patchDestRectOf(pd, ctx);
      withRectExcluded(ctx, dest, () => drawStashOnCtx(obj, ctx, st));
      ctx.drawImage(pd.canvas, 0, 0, pd.canvas.width, pd.canvas.height,
        dest.x0, dest.y0, dest.w, dest.h);
    } else {
      drawStashOnCtx(obj, ctx, st);
    }
    scheduleAsync(obj);
    return;
  }

  // StashPool tier-2 aftermath (2026-07-18): the BASE stash was evicted while
  // a full-res patch covers the visible slice. Draw the patch ALONE — its
  // pixels are the same art the base held, at >= the base's resolution, and
  // its rect covers viewport∩object(+margin), so every VISIBLE pixel is
  // served; the uncovered remainder is off-viewport by construction. The
  // whole-object re-bake is deliberately NOT scheduled: while the patch
  // stands the evaluator owns the lifecycle (re-scheduling would re-bake the
  // exact bytes the pool just freed — the thrash hysteresis exists to
  // prevent). The moment the patch drops (zoom-out / off-viewport / edit /
  // content change) this branch stops matching and the normal miss path
  // re-bakes. Applies to filter stacks too (a bevel patch is the complete
  // base+fx composite of its slice — flat base underneath would double-draw
  // art at AA edges). Never mid-tilt (the trackball needs a whole-object
  // raster; the joystick's object is selected and thus never evicted —
  // belt-and-braces) and never mid-edit (editing dropped the patch anyway).
  if (exportDepth === 0 && !st && obj.__fxPatch && obj.__fxPatch.canvas
    && !obj.__fxDragTilt && !obj.isEditing
    && obj.__fxPatch.baseHash === fxBaseHashOf(obj, steps)) {
    if (bakedRaster) drawSourcedUnderlays(obj, ctx);
    drawPatchOnCtx(obj, ctx, steps);
    return;
  }

  // ownCaching was set by shouldCache() moments ago in render(): false means
  // direct ctx (curved text — EVERY frame) or a parent group's cache. Filters
  // must not run synchronously there: draw plain base now, upgrade async.
  const stashOnly = exportDepth === 0 && obj.ownCaching !== true;
  if (stashOnly) {
    base.call(obj, ctx);
    scheduleAsync(obj);
    return;
  }

  const t0 = performance.now();
  const lease = makeLease(exportDepth === 0);
  try {
    const quality = exportDepth > 0 ? 'full' : 'fast';
    const { canvas, W, H, env } = renderBaseOffscreen(obj, base, geo, scale, quality, lease);
    // Source steps are skipped by the sync chain (no applySync, by contract):
    // with only a source step this paints the plain base — flat while typing,
    // the debounced upgrade below fades the 3D in.
    const result = runChainSync(canvas, steps, env);
    drawResultOnCtx(ctx, result, W, H, geo.halfW, geo.halfH);
    stats.syncRuns += 1;
    stats.lastSyncMs = performance.now() - t0;
  } finally {
    lease.releaseAll();
  }
  if (exportDepth === 0) scheduleAsync(obj);
}

/**
 * PLAIN-PATH die-cut closing — the render body for useCanvasEditor's diecut
 * seam lane (objects with a qualifying border effect but NO data.fx, which
 * never enter the fx render stack). Renders the base into the pooled
 * offscreen at the ctx's device scale, runs the same closing the fx path
 * runs (inside renderBaseOffscreen), and draws the result back.
 *
 * Returns false when there is nothing to do — not a qualifying stroke, a
 * per-frame direct ctx (curved text renders uncached every frame; sync raster
 * work is banned there, same posture as the fx seam's stash-only mode), or a
 * closing that bridged ZERO pixels. On false the caller renders the stock
 * vector base: a clean shape keeps its crisp un-resampled render, and the
 * offscreen round-trip only ever lands on shapes it actually fixes.
 */
export function renderDiecutPlain(obj, ctx, base) {
  if (FX_DISABLED) return false;
  const reachU = diecutStrokeReach(obj);
  if (!(reachU > 0)) return false;
  if (exportDepth === 0) {
    // Allow the object's own cache AND an ancestor group's cache redraw;
    // refuse only the truly uncached per-frame path.
    let cached = obj.ownCaching === true;
    for (let g = obj.group; !cached && g; g = g.group) cached = g.ownCaching === true;
    if (!cached) return false;
  }
  const t = typeof ctx.getTransform === 'function' ? ctx.getTransform() : null;
  const scaleX = t ? Math.hypot(t.a, t.b) : obj.getTotalObjectScaling().x;
  const scaleY = t ? Math.hypot(t.c, t.d) : obj.getTotalObjectScaling().y;
  const scale = Math.max(0.02, (scaleX + scaleY) / 2);
  const geo = fxGeometry(obj, []);
  // fxGeometry's fontSize/2 glyph pad covers most stroke spill; add the
  // reach explicitly so a maxed border on a tiny font can never clip.
  geo.halfW += reachU;
  geo.halfH += reachU;
  const lease = makeLease(exportDepth === 0);
  try {
    const quality = exportDepth > 0 ? 'full' : 'fast';
    const { canvas, W, H, env } = renderBaseOffscreen(obj, base, geo, scale, quality, lease);
    // Keep the crisp stock vector render unless this offscreen actually
    // carries work only IT can show: bridged pockets, or the outline
    // family's outer rings (multi-ring lane — the native render has no way
    // to paint them). rings=1 keeps outlineRinged false — pre-lane behavior.
    if (!env.diecutBridged && !env.outlineRinged) return false;
    drawResultOnCtx(ctx, canvas, W, H, geo.halfW, geo.halfH);
    return true;
  } finally {
    lease.releaseAll();
  }
}

/**
 * PLAIN-PATH union-contour band — the render body for useCanvasEditor's seam
 * lane on objects with a contourOutline-family effect (hollow/trace) but NO
 * data.fx: they never enter the fx render stack, yet their raw-path stroke
 * shows the fonts' internal construction seams. Renders the base through
 * renderBaseOffscreen (where the contour replacement lives — ONE
 * implementation for the fx path, patches, exports and this lane) and draws
 * the banded result back.
 *
 * Returns false when there is nothing to do — not a contour-family object, a
 * per-frame direct ctx (curved text renders uncached every frame; sync
 * raster work is banned there, so LIVE curved hollow/trace keeps the raw
 * degrade — exports still band via the exportDepth bypass), or the contour
 * render degraded (blank text / hiccup: keep the crisp stock vector render
 * instead of a resampled copy of it — the renderDiecutPlain posture).
 */
export function renderContourPlain(obj, ctx, base) {
  if (FX_DISABLED) return false;
  if (!contourOutlineWanted(obj)) return false;
  if (exportDepth === 0) {
    let cached = obj.ownCaching === true;
    for (let g = obj.group; !cached && g; g = g.group) cached = g.ownCaching === true;
    if (!cached) return false;
  }
  const t = typeof ctx.getTransform === 'function' ? ctx.getTransform() : null;
  const scaleX = t ? Math.hypot(t.a, t.b) : obj.getTotalObjectScaling().x;
  const scaleY = t ? Math.hypot(t.c, t.d) : obj.getTotalObjectScaling().y;
  const scale = Math.max(0.02, (scaleX + scaleY) / 2);
  const geo = fxGeometry(obj, []);
  // Band reach past the glyph tight box = strokeWidth/2 (fabric's own
  // fontSize/2 glyph pad already covers every def's max, but the explicit
  // reach is the renderDiecutPlain insurance law).
  const reachU = (Number(obj.strokeWidth) || 0) / 2;
  geo.halfW += reachU;
  geo.halfH += reachU;
  const lease = makeLease(exportDepth === 0);
  try {
    const quality = exportDepth > 0 ? 'full' : 'fast';
    const { canvas, W, H, env } = renderBaseOffscreen(obj, base, geo, scale, quality, lease);
    if (!env.contourBanded) return false; // degraded — keep the vector render
    drawResultOnCtx(ctx, canvas, W, H, geo.halfW, geo.halfH);
    return true;
  } finally {
    lease.releaseAll();
  }
}

// ---------------------------------------------------------------- wrappers
// Shared named functions (not per-object closures): re-attach after clone or
// load can never double-wrap, and detach is a clean `delete`.
function fxWrappedRender(ctx) {
  const base = this.__fxBase || Object.getPrototypeOf(this)._render;
  const steps = this.data && this.data.fx;
  if (FX_DISABLED || this.__fxClipping || !Array.isArray(steps) || steps.length === 0) {
    return base.call(this, ctx);
  }
  try {
    renderFxStack(this, ctx, base, steps);
  } catch (e) {
    logOnce('render failed — fell back to plain render', e);
    base.call(this, ctx);
  }
}

function fxWrappedCacheDims() {
  const dims = this.__fxBaseDims.call(this);
  const steps = this.data && this.data.fx;
  if (FX_DISABLED || !Array.isArray(steps) || steps.length === 0) return dims;
  try {
    const dim = this._getTransformedDimensions({ skewX: 0, skewY: 0 });
    const w = dim.x / (this.scaleX || 1);
    const h = dim.y / (this.scaleY || 1);
    const { mx, my } = marginsOf(this, steps, unitFor(this, w, h), w, h);
    dims.width += Math.ceil(2 * mx * dims.zoomX);
    dims.height += Math.ceil(2 * my * dims.zoomY);
    // Source-step self-heal: once a bake has landed, its REAL extents are
    // known — floor the cache at what the stash actually needs, so even an
    // under-declared bounds() can never clip (the upgrade dirties the
    // object, so the cache is rebuilt through here before the stash draw).
    // Floor AGAINST the margin-grown dims (a filter stash's halfW equals the
    // whole geo coverage incl. fabric's own fontSize pad — comparing it to
    // the tight box would inflate every bevel cache past the perf limit and
    // soften the render, the exact bug this comment guards).
    for (const st of [this.__fxStash, this.__fxExportStash]) {
      if (!st || !st.canvas) continue;
      const reachW = st.halfW + Math.abs(st.ax || 0);
      const reachH = st.halfH + Math.abs(st.ay || 0);
      dims.width = Math.max(dims.width, Math.ceil(2 * reachW * dims.zoomX));
      dims.height = Math.max(dims.height, Math.ceil(2 * reachH * dims.zoomY));
    }
  } catch (e) {
    logOnce('cache-dims spill failed', e);
  }
  return dims;
}

function fxWrappedDrawObject(ctx, forClipping, context) {
  // Flag clip renders so the fx skips them: a bevel's outer highlight band
  // must not grow a clip mask's alpha.
  this.__fxClipping = !!forClipping;
  try {
    return this.__fxBaseDrawObject.call(this, ctx, forClipping, context);
  } finally {
    this.__fxClipping = false;
  }
}

/**
 * Instance shouldCache for IMAGES with fx attached (universal-effects U1).
 * FabricImage.shouldCache() returns needsItsOwnCache() WITHOUT ever setting
 * this.ownCaching ("images do not benefit from caching") — correct for a
 * plain raster, fatal under the seam: ownCaching never reads true, so the
 * fx render sits in stash-only mode forever and every hash-moving render
 * (zoom hop, first apply, drag release) draws the PLAIN raster for a beat —
 * for hideBody band effects that is the icon's body flashing back. While fx
 * is attached the image gets the STANDARD object-cache contract instead
 * (verbatim FabricObject.prototype.shouldCache, fabric 6.9.1) — the sync
 * chain then runs inside cache redraws exactly like text. Instance-only,
 * never serialized; detach deletes it (stock image again).
 */
function fxImageShouldCache() {
  this.ownCaching = (this.objectCaching && (!this.parent || !this.parent.isOnACache()))
    || this.needsItsOwnCache();
  return this.ownCaching;
}

// ---------------------------------------------------------------- attach API
/**
 * Idempotently install the fx interceptor on ONE live fabric object. Captures
 * the PROTOTYPE's methods (never obj._render — re-attach after clone/load must
 * not double-wrap) and installs instance overrides. Reading data.fx happens at
 * render time, so patching params needs no re-attach — just obj.dirty = true.
 */
export function ensureFxRender(obj) {
  if (!obj || obj.__fxAttached) return;
  if (!canaryRan) runCanary();
  const proto = Object.getPrototypeOf(obj);
  if (typeof proto._render !== 'function'
    || typeof proto._getCacheCanvasDimensions !== 'function'
    || typeof proto.drawObject !== 'function') {
    logOnce('attach refused: fabric internals missing on prototype');
    return;
  }
  obj.__fxBase = proto._render;
  obj.__fxBaseDims = proto._getCacheCanvasDimensions;
  obj.__fxBaseDrawObject = proto.drawObject;
  obj.__fxAttached = true;
  obj._render = fxWrappedRender;
  obj._getCacheCanvasDimensions = fxWrappedCacheDims;
  obj.drawObject = fxWrappedDrawObject;
  // Images: standard cache contract while fx is attached (see
  // fxImageShouldCache — without it the seam is stash-only forever and the
  // plain raster flashes on every hash move).
  if (String(obj.type).toLowerCase() === 'image') obj.shouldCache = fxImageShouldCache;
  obj.dirty = true;
  stats.attaches += 1;
}

/** Remove the interceptor and every fx-private instance prop. The object is
 * indistinguishable from a never-touched fabric object afterwards. */
export function removeFxRender(obj) {
  if (!obj || !obj.__fxAttached) return;
  if (obj.__fxTimer) {
    clearTimeout(obj.__fxTimer);
    obj.__fxTimer = null;
  }
  // Volume worker teardown (lane VW): stop a pending swap tick and cancel
  // any in-flight worker jobs for this object's channels. Dynamic import on
  // purpose (the lib/geo lazy-graph law bans only static imports) and only
  // when a channel ever existed — zero cost for every other object.
  if (obj.__fxSwapTimer) {
    clearTimeout(obj.__fxSwapTimer);
    obj.__fxSwapTimer = null;
  }
  if (obj.__fxVolumeChannel || obj.__fxVolumePatchChannel) {
    const chans = [obj.__fxVolumeChannel, obj.__fxVolumePatchChannel].filter(Boolean);
    delete obj.__fxVolumeChannel;
    delete obj.__fxVolumePatchChannel;
    import('../geo/volumeWorkerClient.js')
      .then((m) => chans.forEach((ch) => m.cancelChannel(ch)))
      .catch(() => {});
  }
  // Viewport patch teardown FIRST — restores the pre-patch objectCaching.
  dropPatch(obj);
  delete obj._render;
  delete obj._getCacheCanvasDimensions;
  delete obj.drawObject;
  if (obj.shouldCache === fxImageShouldCache) delete obj.shouldCache;
  delete obj.__fxAttached;
  delete obj.__fxBase;
  delete obj.__fxBaseDims;
  delete obj.__fxBaseDrawObject;
  delete obj.__fxStash;
  delete obj.__fxExportStash;
  delete obj.__fxGen;
  delete obj.__fxLastScale;
  delete obj.__fxClipping;
  delete obj.__fxDragTilt;
  delete obj.__fxPatchWant;
  delete obj.__fxPatchBusy;
  delete obj.__fxBakeBusy;
  delete obj.__fxSeenTick;
  delete obj.__fxSwapTimer;
  delete obj.__fxVolumeBusy;
  delete obj.__fxVolumeLastEnd;
  obj.dirty = true;
  stats.detaches += 1;
}

/**
 * Immediate (undebounced) full-quality upgrade — the 3D joystick's throttled
 * drag bakes and its release bake ride this instead of the 150ms debounce
 * (a continuous drag resets the debounce forever, so it alone would never
 * bake mid-drag). scaleMul < 1 bakes at reduced resolution for cheap drag
 * feedback; the result lands even if the params moved on mid-bake
 * (allowStale — see runAsyncUpgrade), and force guarantees the release bake
 * replaces a same-hash low-res drag stash. Resolves when the bake landed
 * (or was superseded); never rejects.
 */
export function fxRequestUpgrade(obj, { scaleMul = 1 } = {}) {
  if (!obj) return Promise.resolve();
  if (obj.__fxTimer) {
    clearTimeout(obj.__fxTimer);
    obj.__fxTimer = null;
  }
  return runAsyncUpgrade(obj, { scaleMul, allowStale: true, force: true })
    .catch((e) => logOnce('requested upgrade failed', e));
}

// ---------------------------------------------------------------- export mode
/** Print export: run chains synchronously at FULL quality (the export raster
 * IS the deliverable — no async upgrade will follow) and leave stashes alone.
 * exportWorkspaceBlob brackets its render with these. Re-entrant. */
export function fxBeginExport() { exportDepth += 1; }
export function fxEndExport() { exportDepth = Math.max(0, exportDepth - 1); }

/**
 * Export pre-pass for source steps (WebGL cannot run synchronously inside
 * the export render): bake this object's full chain at `pixelScale` device
 * px per object unit — the print multiplier × the object's own scaling,
 * long edge capped by the effect itself (extrude: 4096) and the stash area
 * cap — and park it on __fxExportStash keyed by the SCALE-FREE hash. The
 * export render draws it; fxCleanupExport (caller's finally, mandatory)
 * frees it so a multi-MB print bake never outlives the export.
 *
 * Returns {W, H} when a bake was primed, null when the object has no source
 * steps / isn't attached. Never throws: a failed bake leaves no export stash
 * and the object exports flat (same posture as the screen path).
 */
export async function fxPrimeExport(obj, pixelScale) {
  if (FX_DISABLED || !obj || !obj.__fxAttached) return null;
  const steps = obj.data && obj.data.fx;
  // Source steps AND puffy sticker stacks (sticker v3): both need WebGL,
  // which the export render cannot run synchronously — pre-bake here.
  if (!Array.isArray(steps) || steps.length === 0
    || !(hasSourceStep(steps) || stickerWantsGlBake(steps))) return null;
  try {
    const scale = Math.max(0.05, Number(pixelScale) || 1);
    const geo = fxGeometry(obj, steps);
    const lease = makeLease(false);
    const res = await runChainFull(obj, steps, geo, scale, lease);
    if (!res) return null;
    if (res.W * res.H > renderBoundsNow().maxArea * 1.05) {
      // Defensive only — every producer caps at these bounds upstream.
      logOnce('fxPrimeExport: bake exceeded the area budget — exported flat');
      return null;
    }
    // No-silent-caps: when the runtime ceilings delivered fewer px than the
    // print multiplier needs, say so with numbers. Since the print300 lane
    // (tiled GL bakes + 16384/96Mpx export budgets) this fires only for an
    // fx object PHYSICALLY larger than the stash budget itself.
    const deliveredPPU = res.W / (res.halfW * 2);
    if (deliveredPPU < scale * 0.95) {
      const b = renderBoundsNow();
      warnOnce(`prime-short:${Math.round(res.halfW)}:${Math.round(scale * 10)}`,
        `export prime under-resolved: delivered ${res.W}px over ${Math.round(res.halfW * 2)}u `
        + `(${deliveredPPU.toFixed(2)} px/u) vs needed ${scale.toFixed(2)} px/u — runtime cap bound `
        + `(${JSON.stringify(b)}). PRINT300_TODO: an fx object needing >${b.maxSide}px on a side `
        + `(>${Math.round((b.maxSide / 300) * 10) / 10}in at 300dpi) or >${b.maxArea}px² of stash `
        + `delivers the cap; per-strip stash slices would be the next step.`);
    }
    obj.__fxExportStash = {
      hash: fxBaseHashOf(obj, steps),
      canvas: res.canvas,
      W: res.W,
      H: res.H,
      halfW: res.halfW,
      halfH: res.halfH,
      ax: res.ax,
      ay: res.ay,
      reqScale: scale,
    };
    stats.exportPrimes += 1;
    return { W: res.W, H: res.H };
  } catch (e) {
    logOnce('fxPrimeExport failed — object exports flat', e);
    return null;
  }
}

/** Drop the print-resolution bake (and let GC take the canvas). Safe to call
 * unconditionally on every object after an export. */
export function fxCleanupExport(obj) {
  if (obj && obj.__fxExportStash) delete obj.__fxExportStash;
}

/**
 * Export/warm-up asset prime for FILTER steps (source steps ride
 * fxPrimeExport's bake). A filter step's applySync may paint a PLACEHOLDER
 * while an async-decoded asset (a sticker plate's texture tile) loads —
 * harmless on screen (the debounced upgrade repaints) but a HAZARD for a COLD
 * export, whose synchronous render IS the deliverable with no upgrade to
 * follow. Each def may expose primeAssets(params, obj) → a Promise (or an
 * array of them, or null) that resolves when its assets are decode-ready; this
 * awaits them all so the export render paints real pixels, not the placeholder.
 * Calling a def's primeAssets also STARTS a not-yet-requested decode (a plate
 * that never rendered on screen). Never throws: an un-warmed asset falls back
 * to the placeholder — the same posture as the screen path.
 *
 * Returns the number of assets awaited (0 = nothing pending — zero cost for a
 * solid/gradient plate or any assetless stack).
 */
export async function fxPrimeAssets(obj) {
  if (FX_DISABLED || !obj) return 0;
  const steps = obj.data && obj.data.fx;
  if (!Array.isArray(steps) || steps.length === 0) return 0;
  const pending = [];
  for (const step of steps) {
    const def = getFx(step && step.id);
    if (!def || typeof def.primeAssets !== 'function') continue;
    try {
      const params = def.sanitize(step.params || {});
      if (def.isEmpty(params)) continue;
      const r = def.primeAssets(params, obj);
      if (Array.isArray(r)) { for (const pr of r) if (pr) pending.push(pr); }
      else if (r) pending.push(r);
    } catch (e) {
      logOnce(`primeAssets(${step.id}) threw`, e);
    }
  }
  if (!pending.length) return 0;
  await Promise.all(pending.map((pr) => Promise.resolve(pr).catch(() => {})));
  return pending.length;
}

// ---------------------------------------------------------------- canary
// A tiny throwaway StaticCanvas proves the whole seam end-to-end (prototype
// hooks exist, wrapper runs, filter pixels land) before the first real attach.
// Failure = fabric internals moved under us → disable everything loudly and
// let every attached object fall back to stock rendering.
registerFx({
  id: '__fxCanary',
  version: 1,
  kind: 'filter',
  spill: () => 2,
  applySync(canvas, env) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const img = ctx.getImageData(0, 0, env.width, env.height);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] > 0) {
        d[i] = 255 - d[i];
        d[i + 1] = 255 - d[i + 1];
        d[i + 2] = 255 - d[i + 2];
      }
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
  },
});

function runCanary() {
  canaryRan = true;
  let sc = null;
  try {
    if (typeof Textbox.prototype._render !== 'function') {
      throw new Error('Textbox.prototype._render is not a function');
    }
    if (typeof Textbox.prototype._getCacheCanvasDimensions !== 'function') {
      throw new Error('Textbox.prototype._getCacheCanvasDimensions missing');
    }
    sc = new StaticCanvas(undefined, {
      width: 64, height: 40, renderOnAddRemove: false, enableRetinaScaling: false,
    });
    const tb = new Textbox('Fx', {
      left: 32, top: 20, originX: 'center', originY: 'center',
      fontSize: 26, fill: '#101010', fontFamily: 'Arial', width: 60,
    });
    sc.add(tb);
    sc.renderAll();
    const read = () => sc.lowerCanvasEl
      .getContext('2d', { willReadFrequently: true }).getImageData(0, 0, 64, 40).data;
    const flat = read().slice();
    const errsBefore = stats.errors;
    tb.data = { fx: [{ id: '__fxCanary', params: {} }] };
    // Attach directly — ensureFxRender would recurse into this canary.
    tb.__fxBase = Object.getPrototypeOf(tb)._render;
    tb.__fxBaseDims = Object.getPrototypeOf(tb)._getCacheCanvasDimensions;
    tb.__fxBaseDrawObject = Object.getPrototypeOf(tb).drawObject;
    tb.__fxAttached = true;
    tb._render = fxWrappedRender;
    tb._getCacheCanvasDimensions = fxWrappedCacheDims;
    tb.drawObject = fxWrappedDrawObject;
    tb.dirty = true;
    sc.renderAll();
    const fxd = read();
    if (stats.errors > errsBefore) throw new Error('fx wrapper threw during canary render');
    let changed = false;
    for (let i = 0; i < flat.length; i += 4) {
      if (Math.abs(flat[i] - fxd[i]) > 16) { changed = true; break; }
    }
    if (!changed) throw new Error('fx produced no pixel change');
    if (tb.__fxTimer) { clearTimeout(tb.__fxTimer); tb.__fxTimer = null; }
    canaryResult = 'ok';
  } catch (e) {
    canaryResult = `failed: ${e && e.message ? e.message : e}`;
    FX_DISABLED = true;
    console.error(
      '[fx] LIVE EFFECTS DISABLED — the fabric render seam failed its self-test. '
      + 'All objects render stock fabric. Did the fabric version change?', e,
    );
  } finally {
    try { sc?.dispose(); } catch (_) { /* ignore */ }
  }
}

/** Re-runnable self-test — clears the disabled flag first so a hot-reloaded
 * fix can re-enable without a full page reload. Returns 'ok' or 'failed: …'. */
export function fxSelfTest() {
  FX_DISABLED = false;
  canaryRan = false;
  runCanary();
  return canaryResult;
}

// ---------------------------------------------------------------- dev surface
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  window.__cceFx = {
    get enabled() { return !FX_DISABLED; },
    get canary() { return canaryResult; },
    // attaches minus explicit detaches. loadFromJSON rebuilds instances
    // without a detach, so this can over-count across undo/redo — assert
    // object-level truth (obj.__fxAttached) when that matters.
    get attachCount() { return stats.attaches - stats.detaches; },
    stats,
    selfTest: fxSelfTest,
    _config: config, // probes assert export limit restore through this
    // Runtime resolution ceilings (probe-resolution asserts delivered px
    // against these) + the standing-bake caps for recipe images.
    get caps() { return { ...getRenderCaps(), bakeStand: { ...BAKE_STAND_CAPS } }; },
    // StashPool governance stats (probe-schoolpc asserts budget + eviction
    // laws): bytes = standing stash+patch total at the last check, budget =
    // the deviceMemory ladder value, evictions/tier2/evictedBytes/overBudget
    // as accounted. _poolCheckNow forces an undebounced governance pass.
    get pool() { return { ...pool }; },
    _poolCheckNow: (canvas) => poolCheck(canvas || window.__cceCanvas),
    // Union-contour band surface (probe-contour asserts trace cadence,
    // cache-hit ratio and the degrade count stays 0 on the happy paths).
    get contour() { return getContourOutlineStats(); },
    // Sticker v3 surface (probe-n1): exact-vs-raster plate routing + the
    // puffy bake/degrade ledger.
    get sticker() { return getStickerShapeStats(); },
    // Budgets-ladder drill (probe-viewpatch): re-probe the caps after the
    // probe mocks navigator.deviceMemory. Test-only.
    _resetRenderCapsForTest,
    // Viewport-patch drill surface: force an immediate (undebounced) eval.
    _evalPatchesNow: (canvas) => evaluateCanvasPatches(canvas || window.__cceCanvas),
    // The LIVE registry instance. Probes must use this instead of importing
    // fxRegistry.js by URL: after an HMR edit the page graph holds a
    // ?t=-stamped module, and a plain-URL import would build a second, EMPTY
    // registry.
    getFx,
    // Context-loss drill (probe #9): forces the SHARED 3D renderer's WebGL
    // context lost via the standard extension. Dynamic import on purpose —
    // three.js must never ride the editor boot chunk.
    loseWebglContext: async () => {
      const m = await import('../text3d/renderer3d.js');
      const gl = m.getRenderer().getContext();
      const ext = gl.getExtension('WEBGL_lose_context');
      if (!ext) return false;
      ext.loseContext();
      return true;
    },
  };
}
